import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useAnalyticsRole } from "../lib/useAnalyticsRole";
import type {
  DisplayCommand,
  GoonSession,
  ID,
  MediaItem,
  Region,
  Channel,
  PartnerInfo,
  SoundMode,
  SourceConfig,
  View,
  InfinityAutoScrollSec,
} from "@gooner/shared";
import {
  DEFAULT_SETTINGS,
  INFINITY_DEFS,
  MONITOR_TEMPLATES,
  WALL_DEFS,
  adaptiveFramingOn,
  audibleCandidates,
  degradeRegions,
  builtinViewId,
  hoverSolo,
  infinityIdOfView,
  isViewWritable,
  keywordGroups,
  pickSolo,
  radioAudible,
  regionPhotoSeconds,
  regionVolume,
  soundModeFromWire,
  soundModeMuted,
  soundsHere,
  sourceGain,
  syncLeaderOf,
  transposeRegions,
  viewIdKind,
  wallIdOfRegion,
  wallIdOfView,
  withSoundDefault,
  BAR_CUTOFF_PX,
  FULL_IMAGE_SECONDS,
} from "@gooner/shared";
import { facetCombo } from "../lib/viewShape";
import { api, setSourceDevice, userApi } from "../lib/api";
import { roomViewId } from "@gooner/shared";
import { useRoom, type RoomCtx } from "./room/useRoom";
import { useRoomMirror } from "./room/useRoomMirror";
import { useServerClockOffset } from "../lib/serverClock";
import type { DrivenBy } from "./RegionTile";
import { useIsPortrait } from "../lib/useIsPortrait";
import { useHorizontalSwipe } from "../lib/useHorizontalSwipe";
import { readInfinityAutoScroll, writeInfinityAutoScroll } from "../lib/infinityAutoScroll";
import { GoldDialog } from "../control/components/GoldDialog";
import { FapsDialog } from "../control/components/FapsDialog";
import { FapsPill } from "../components/FapsPill";
import { FAPS_AUTH_REASON, type FapsOpenSource, useFapsEnabled, useFapsStore } from "../lib/faps";
import { track } from "../analytics";
import {
  editorsChoiceSourceOf,
  loadEditorsChoice,
  regionSourceCollectionProps,
  viewPlayProps,
  type EditorsChoiceIndex,
  type ViewPlayProps,
} from "../lib/viewEvents";
import { reportClipView } from "../lib/clipViews";
import { useSession } from "../lib/session";
import { type DisplayConn, connectDisplay } from "../lib/ws";
import { crownHit } from "./RegionFapOverlay";
import { RegionEditHint } from "./RegionEditHint";
import { RegionTile } from "./RegionTile";
import { STAGES } from "./stages";
import { SessionSummaryModal } from "./SessionSummaryModal";
import { CHIP_ROW_PX, SESSION_TAB_PX, ScreenMenuItems, ScreenThumbBar, chipCovers } from "./ScreenThumbBar";
import { useScreenIdle } from "./useScreenIdle";
import { useTapIdle } from "./useTapIdle";
import { ConfirmDialog } from "../control/components/ConfirmDialog";
import type { FapBus, RegionController, RegionRegistry } from "./types";
import { MenuAccountItems } from "../control/components/AccountControl";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useFullscreen } from "./useFullscreen";
import { SiteBar, type CodeBubble } from "../components/SiteBar";
import {
  WhatsPlayingSheet,
  pointCollectionEntry,
  useWhatsPlayingEntries,
} from "../components/WhatsPlaying";
import { AuthModal } from "../control/components/AuthModal";
import { ScreenControls } from "./ScreenControls";
import { PartyBadge } from "./PartyBadge";
import { MasterAudioPanel } from "./MasterAudioPanel";
import { useRadio } from "./useRadio";
import { trackAudioMode, trackAudioPanel, trackChannelPlayed } from "../lib/channels";
import { PartnerDialog } from "../components/PartnerDialog";
import { ScreenSettingsDialog } from "./ScreenSettingsDialog";
import type { PartyState } from "../lib/usePendingScreen";
import { SyncBus } from "./syncBus";
import { RegionHoverChrome } from "./RegionHoverChrome";
import { WallBuyDialog } from "./WallBuyDialog";
import { SurfaceGuide } from "../components/SurfaceGuide";
import {
  GUIDES,
  INFINITY_BATTLE_GUIDE,
  PREMIUM_WALL_GUIDE_FAPS,
  SCREEN_GUIDE_FAPS,
  WALL_GUIDE_FAPS,
  guideDismissed,
  namedScreenGuide,
  wallGuide,
} from "../components/guides";
import { WallRegionChrome } from "./WallRegionChrome";
import { useWallState } from "./useWallState";
import { RegionFeedDialog } from "./RegionFeedDialog";
import { syncPeers, syncTintMap } from "../control/lib/syncTints";
import { useCollectionOptions, useContentTags } from "../control/lib/feedData";
import { linkRegions, toggleMirror, unlinkAdoptShared } from "../control/lib/regionSync";
import { FapsCapNudge } from "../components/FapsCapNudge";
import { FapsUnseatedCard } from "../components/FapsUnseatedCard";
import type { GoldUpsellGate } from "../lib/gold";
import { trackGoldGate, useGoldGate } from "../lib/gold";
import { usePublicSettings } from "../lib/publicSettings";
import { seedWallAllowance, useWallAllowance, utcDayOf } from "../lib/wallAllowance";
import { flushWatchSession, setWatchOnWall, setWatchSession, watchPause, watchResume } from "../lib/watchReport";
import { WallAllowanceGauge } from "./WallAllowanceGauge";

/** How long one identify pulse holds the overlay. The remote re-pulses every ~10s while
 *  its row stays selected (see Goontroller), so the overlay is continuous under a live
 *  selection and self-clears within this window if the remote silently dies. An explicit
 *  {on:false} — deselection — drops it immediately. */
const IDENTIFY_MS = 25_000;
/** How often a screen measures latency (which doubles as its presence heartbeat). */
const PING_MS = 5000;
/** How long a connected socket may go without handing over a view before the wrapper is
 *  told. Generous: this is a fault signal, not a timeout on a slow server. */
const NO_CONFIG_MS = 8000;

/** A pointer over full-bleed video is noise the moment it stops moving. */
const CURSOR_IDLE_MS = 3000;

/**
 * How long the wall's price badges stay up once a screen goes quiet.
 *
 * Longer than the cursor's delay on purpose: a cursor is in the way the moment it
 * stops, while a price is the reason the badge exists — it should be readable by
 * somebody who just glanced over, and only then get out of the picture. The wall is
 * the one view whose chrome is permanent (WallRegionChrome), so it is the one that
 * needs a way to stop being permanent.
 */
const WALL_BADGE_IDLE_MS = 5000;

/**
 * How long the phone's thumb bar stays up once a screen goes quiet.
 *
 * The longest of the three, and for the opposite reason to the cursor's: this one is not
 * in the way, it is the only way. A phone is frequently the ONLY device in the room, so
 * the bar it fades is the whole control surface — losing it mid-reach costs a tap to get
 * back, and a control that punishes hesitation trains people not to use it. Four seconds
 * is long enough to read the view's name and decide, short enough that a screen left alone
 * settles down to pure media.
 */
const THUMB_IDLE_MS = 4000;

/** Longest gap between two taps that still reads as one gesture — the platform's
 *  double-tap rhythm. (The catalog grid read the same window once; its clip cards open on
 *  a single click now.) */
const DOUBLE_TAP_MS = 300;



/**
 * The fullscreen view panel. Driven by a view over a WebSocket.
 *
 * Every screen is a device on a view — there is no longer a second, in-memory mode. Play
 * collection and Play random used to hand a View straight in as `ephemeralView`, which
 * made a whole parallel path with no id, no device row and no session; they mint a real
 * temporary view and redirect here now. See MintedScreen.
 *
 * `viewId` / `deviceId` come from the caller, which is always `DeviceScreen`: it resolves
 * the device from /view/d/:id first and hands both down rather than round-tripping them
 * through the URL. They stay optional so the guards below can stand in for the window
 * before a device resolves.
 *
 * `onConnected` reports the socket's status upwards. The device screen chooses between
 * "playing" and "disconnected" with it; nothing else inside this component changes.
 *
 * ONE BAR — the same SiteBar every page wears, with this screen's cluster injected into
 * its middle: what is playing, Edit, fullscreen, and the goon session (see
 * ScreenControls). It never fades and never folds; the fullscreen toggle is the one
 * gesture that removes it, and it removes it entirely. The controls that belong to the
 * person watching — the per-region editor and the hover ring — still gate on movement
 * and go quiet after CONTROLS_IDLE_MS, so a wall TV nobody touches never shows them.
 *
 * There is no `chromeless` any more because there was no second chrome: every mount of
 * this component passed it (MintedScreen navigates to /view/d/:id rather than rendering
 * here), so the TopNav-and-PingBadge branch it guarded was dead code wearing a prop.
 */
/*
 * THE CHANNEL LIST, hard-coded until `GET /api/channels` exists.
 *
 * A constant with the REAL shape rather than a mock: moving it behind the endpoint is a
 * swap of this one binding, not a rewrite of anything that reads it. The audio and the
 * artwork are real too — the only thing still missing is the fetch.
 *
 * The track is split rather than offset. `audioUrl` is the looping body and `introUrl` the
 * 50 seconds that should not come round again; the browser loops the body itself, which is
 * why there is no loop offset anywhere in this file. Decoding a twelve-minute channel
 * through Web Audio to get sample-accurate loop points would have cost 267 MB of PCM
 * resident in the browser, which is not a trade on the sticks these walls run on.
 */
const CHANNELS: readonly Channel[] = [
  {
    id: "radio-gooner",
    name: "Goddess Hannah",
    artist: "Gooning Indoctrinations",
    artUrl: "/radio/radio-gooner.jpg",
    audioUrl: "/radio/radio-gooner.m4a",
    introUrl: "/radio/radio-gooner-intro.m4a",
    partner: {
      id: "goon-indoctrination-audios",
      name: "Goon Indoctrination Audios",
      tagline: "Hypnotic Lessons and Conditioners.",
      blurb:
        "Goon Indoctrination Audios create hypnotic Lessons and Conditioners that push gooners deeper into their porn addictions. Obey the audio files and accept your brain damage. Original music, scripts, voiced by Goddess Hannah.",
      imageUrl: "/partners/goon-indoctrination-audios.jpg",
      links: [
        {
          label: "Her Patreon — from $10/month",
          url: "https://www.patreon.com/cw/goonindoctrinationaudios",
          kind: "support",
        },
      ],
    },
  },
];

/** No frame: the glass as it is. */
const unframed = (glass: ReactNode): ReactNode => glass;

export function DisplayApp({
  viewId: viewIdProp,
  deviceId,
  onConnected,
  onNoView,
  onForked,
  pairing,
  party,
  roomId,
  frame,
  back,
  onPlaying,
}: {
  viewId?: string;
  deviceId?: string;
  /** A GOON ROOM (2026-09-19): this screen is the room's. The host runs their real view
   *  through every path /screen has and mirrors it; a member follows. The view id is the
   *  room's to say (useRoom), never the caller's. */
  roomId?: string;
  /** The room's frame — roster, chat, cards — drawn AROUND the glass, under the bar. */
  frame?: (glass: ReactNode, room: RoomCtx | null) => ReactNode;
  onConnected?: (connected: boolean) => void;
  /** Fired once when a socket has been up for a while and never produced a view. */
  onNoView?: () => void;
  /**
   * Where a copy went, for a panel that has no device to retarget.
   *
   * Editing an unwritable view copies it onto this screen. A screen with a device does
   * that by pointing the device at the copy and reloading; an ANONYMOUS panel — the
   * pending screen, which plays a quick setup while it waits to be claimed — has no device
   * row, so the caller owns which view is mounted here and is handed the copy instead.
   */
  onForked?: (viewId: string) => void;
  /** The screen's code for the site bar's right-side bubble, wired to the big code card
   *  the caller owns. The pending screen hands in the code that MINTS a device; a
   *  claimed screen (DeviceScreen) the code that ADOPTS the device it already is. */
  pairing?: CodeBubble | null;
  /**
   * This screen's party settings and the hold running on it, from the same hook that owns
   * its code — handed in rather than reached for, like everything else here, so the panel
   * still renders in a check with no socket and no router.
   *
   * Absent on a surface that is not a claimable screen at all, which is also what takes
   * the gear out of the bar: no party state, no settings to open.
   */
  party?: PartyState | null;
  /** Handed straight to the SiteBar's `back` slot (beside the mark). See SiteBar. */
  back?: ReactNode;
  /**
   * Told which view this screen is on, at mount and on every switch. The device page
   * uses it to keep Logo / Menu Home pointing at the last mode: a device retargets
   * through `setShowing`, so nothing else on the page ever hears about a switch.
   */
  onPlaying?: (viewId: string) => void;
} = {}) {
  /*
   * WHAT THIS SCREEN IS PLAYING — state, seeded from the prop, not the prop itself.
   *
   * This one line is what stops a retarget from restarting the page. The socket effect
   * below already keys on `viewId`, so writing to it closes the old socket and opens one
   * joined to the new view's room — precisely what `location.reload()` was being used to
   * achieve, minus the reload. And the reload's collateral was fullscreen: leaving it is
   * automatic on any navigation and re-entering needs a fresh user gesture, so changing
   * what a fullscreen television played used to knock it out of fullscreen.
   *
   * The effect keeps it following the prop, for the caller that owns which view is mounted
   * here (the pending screen, which has no device to retarget).
   */
  const [viewId, setViewId] = useState(viewIdProp ?? (roomId ? roomViewId(roomId) : undefined));
  useEffect(() => {
    if (!roomId) setViewId(viewIdProp);
  }, [viewIdProp, roomId]);
  const onPlayingRef = useRef(onPlaying);
  onPlayingRef.current = onPlaying;
  useEffect(() => {
    if (viewId) onPlayingRef.current?.(viewId);
  }, [viewId]);

  // Every playing surface — claimed, pending, minted — mounts this, so this is where a tab
  // becomes a "screen" to analytics.
  useAnalyticsRole("screen");

  const [view, setView] = useState<View | null>(null);
  const portrait = useIsPortrait();
  /**
   * Adaptive layout: the regions this screen DRAWS, which are the authored ones
   * unless the view opted in and this screen's orientation disagrees with the
   * authored aspect — then the transpose (columns become rows). Render-only:
   * `view`/`viewRef` always hold authored geometry, so `applyRegionEdit` writes
   * back what the editor authored, never what this screen happened to show.
   */
  const shownRegions = useMemo(() => {
    if (!view) return null;
    const authoredPortrait = view.aspect.w < view.aspect.h;
    return view.adaptiveLayout && authoredPortrait !== portrait
      ? transposeRegions(view.regions)
      : view.regions;
  }, [view, portrait]);
  // The same badge colours the editor's canvas deals to sync groups, over the AUTHORED
  // regions — a transpose changes geometry, never identity, and the tint is identity.
  const syncTints = useMemo(() => syncTintMap(view?.regions ?? []), [view]);
  // Who lights up with whom: hovering any member of a sync group rings the whole
  // group (see the ring below), so "these tiles are one feed" is visible before
  // anything is clicked. Authored regions, same as the tints — identity, not geometry.
  const peerIds = useMemo(() => syncPeers(view?.regions ?? []), [view]);
  const [connected, setConnected] = useState(true);
  const [paused, setPaused] = useState(false);
  /*
   * SOUND HAS TWO OWNERS, and this flag is the senior one. Whether this SCREEN makes
   * noise at all is the room's call and lives right here — ephemeral, defaulting to
   * muted (the state no browser refuses and no room is surprised by), toggled by the
   * bar's red button or the remote's `mute` command. WHICH region carries the audio,
   * and how loud, is the LAYOUT's call and saves into the view (Region.soundOn/volume,
   * default silent, edited from each tile's hover chrome). Each tile is handed the AND
   * of the two, so nothing a view says can make a muted screen speak — and the tiles
   * tell us when the browser refused sound, so neither owner's button ever lies.
   *
   * THE SENIOR OWNER IS NOW FOUR-VALUED, because on a sixteen-panel wall a boolean was
   * too blunt: unmuting meant every soundOn panel spoke at once. `muted` and `all` are
   * the two states it always had; `one` plays a single panel (see soloId) and `radio`
   * plays the channel with every panel silent. Everything downstream still asks the
   * yes/no question through `muted` below, so the wire, the presence record and the
   * remote's button are untouched by the widening.
   */
  const [soundMode, setSoundMode] = useState<SoundMode>("muted");
  /** The boolean shadow of the mode, for every reader that only ever asked "is it quiet":
   *  the bar's red button, the socket report, the presence record the remote reads. */
  const muted = soundModeMuted(soundMode);
  /** The master fader, multiplying every source's own level — see sourceGain. */
  const [master, setMaster] = useState(1);
  /** Play 1's standing pick: the one panel carrying sound. Null = nothing on this wall
   *  is eligible, which is silence and is the honest answer. */
  const [soloId, setSoloId] = useState<ID | null>(null);
  /*
   * Whether the panel Play 1 picked is ACTUALLY making noise, which is not the same as
   * "a pick exists". pickSolo holds the first candidate when nothing is audible yet — so
   * that sound arrives the instant one of them reveals a video, with no fresh trigger —
   * which means a standing pick is a poor proxy for "you can hear something". The panel
   * says "waiting for a panel with audio" off this, and off the pick it would never have
   * said it at all.
   */
  const [soloAudible, setSoloAudible] = useState(false);
  const [identifying, setIdentifying] = useState(false);
  const [hovered, setHovered] = useState<ID | null>(null);
  /** The tap the tile's onClick is adjudicating — see the tap contract at the tile. EVERY
   *  field is captured at pointerdown: by click time the tap's own synthetic mouseenter
   *  has already moved `hovered`, and any overlay the tap dismissed has already closed
   *  itself. A tap that landed on the crown records nothing at all. */
  const touchTap = useRef<{ id: ID; wasSelected: boolean; dismiss: boolean } | null>(null);
  /** The tap before this one, for the double that opens the picker: which tile, and when.
   *  ONE ref for the whole wall — the tiles are a map inside this component — so the id
   *  has to travel with it, or a quick tap on one tile then another would open the second
   *  on the strength of the first. */
  const lastTap = useRef<{ id: ID; at: number }>({ id: "", at: 0 });
  /**
   * Is the wall being driven by a finger? ON TOUCH, MOUSELEAVE MEANS NOTHING.
   *
   * A finger never leaves — it lands, lifts, and the browser invents a mouse burst around
   * that, including mouseouts with no consistent shape or timing. `hovered` used to be
   * cleared by those, which is why there was no dependable deselect on a phone at all:
   * the selection could evaporate a beat after a tap for reasons the user never performed.
   * Now a tap owns selection outright on touch, and hover owns it on a mouse, and the two
   * do not get to overrule each other. Written at pointerdown, which is the only place the
   * pointer's type is on the record.
   */
  const touchPointer = useRef(false);
  /**
   * A region whose hover chrome must stay mounted even though the pointer has left the
   * tile: the link menu portals BELOW the tile and a volume drag strays past its edge,
   * and `hovered` clearing on mouseleave would unmount the very control being used.
   * Set by the chrome's onLockChange around those two interactions, cleared after.
   */
  const [chromeLock, setChromeLock] = useState<ID | null>(null);
  /**
   * The link menu's candidate row under the pointer: that region gets the full hover
   * treatment (ring + chrome) across the glass, answering "which tile would I be
   * linking to?" without reading names. Cleared by RegionLinkButton on every
   * menu-close path, and again when the chrome's lock releases.
   */
  const [linkPeek, setLinkPeek] = useState<ID | null>(null);
  /**
   * Latched true by the first hover, never back. The hover chrome and the region popup
   * label feeds with the catalog (collections + tags), but a wall TV nobody ever
   * touches must not fetch a catalog nobody will see — so the two hooks below sit
   * disabled until a pointer proves somebody is here.
   */
  const [wantFeedData, setWantFeedData] = useState(false);
  const { collections, loading: collectionsLoading } = useCollectionOptions(wantFeedData);
  const { tags: contentTags, loading: tagsLoading } = useContentTags(wantFeedData);
  // The region feed popup, keyed by region id; null = closed. THIS name on purpose:
  // the retired on-screen region editor's state carried the generic "editing" name,
  // and the screen-state check hunts that name to keep the old editor from growing
  // back — this popup is the narrow feed picker, not that editor.
  const [feedEditRegionId, setFeedEditRegionId] = useState<ID | null>(null);
  // The wall's buy dialog, keyed the same way — the click that opens the feed popup
  // on any other view opens THIS on the Community Wall.
  const [wallBuyRegionId, setWallBuyRegionId] = useState<ID | null>(null);
  const [buyParams, setBuyParams] = useSearchParams();
  // Restated on the `?buy=` scrub below: a replace that omits it drops the entry's state,
  // and a screen's origin lives there (see lib/screenOrigin).
  const routeState = useLocation().state as unknown;
  const routeStateRef = useRef(routeState);
  routeStateRef.current = routeState;
  const [forking, setForking] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [session, setSession] = useState<GoonSession | null>(null);
  // The just-ended session shown in the summary popup (on the device that stopped it).
  const [summary, setSummary] = useState<{ session: GoonSession } | null>(null);
  // The what's-playing picker, opened by the bar's Now Showing bubble (device screens
  // only — see onLabelPress below).
  const [picker, setPicker] = useState(false);
  // Latest active-ness, read by the view recorder without re-subscribing.
  const sessionActiveRef = useRef(false);
  sessionActiveRef.current = session !== null;
  // Remembers each region's source before it was locked to a clip, so "un-repeat"
  // can restore it instead of guessing.
  const prevSourceRef = useRef<Map<ID, SourceConfig>>(new Map());

  /*
   * THREE idle clocks, each its own timer rather than a derivative of another's — the
   * hook takes a delay so the caller can name the meaning, and folding any two of these
   * into one delay would mean choosing which one is wrong.
   *
   *   cursor       a pointer over full-bleed video is noise the moment it stops
   *   wall badges  a price should be readable by someone who just glanced over
   *   thumb bar    the phone's controls, which on a lone phone are the only ones
   *
   * A fourth clock was retired: the "controls" clock that gated the click-to-edit
   * affordances. The on-screen editor is gone — a click on a region means a fap now — so
   * nothing was left for it to gate. The fap overlay never idled, and the SITE BAR still
   * neither fades nor folds; fullscreen is the one gesture that removes it.
   */
  const cursorHidden = useScreenIdle(CURSOR_IDLE_MS);
  const wallBadgesIdle = useScreenIdle(WALL_BADGE_IDLE_MS);
  const screenThumbIdle = useScreenIdle(THUMB_IDLE_MS);
  const scrollThumbIdle = useTapIdle(THUMB_IDLE_MS);
  const { isFullscreen, toggle: toggleFullscreen, supported: canFullscreen } = useFullscreen();
  /**
   * What this screen is playing, for the now-playing badge.
   *
   * Read off the live view rather than the route, so it follows a retarget without a
   * reload — the controller changing what is on the wall is exactly when somebody looks up
   * to see what changed.
   *
   * Built-ins carry their emoji on the template rather than on the view, so it is resolved
   * here; a saved view keeps whatever icon its owner chose, and a temporary one may have
   * none, in which case the name stands alone.
   */
  const viewLabel = useMemo(() => {
    /*
     * Prefer the live view when it MATCHES what we asked to play. A mode switch updates
     * viewId (and tears the socket) a frame before the new config arrives — keeping the
     * previous view's name on the pill is the "Solo Girls" stuck-label bug (2026-10-07
     * QA). While config is in flight, resolve known doors from viewId so the chrome
     * tracks the pick immediately.
     */
    const id = view && viewId && view.id === viewId ? view.id : viewId;
    if (!id) return null;
    const builtin = MONITOR_TEMPLATES.find((t) => builtinViewId(t.id) === id);
    const wall = wallIdOfView(id) ?? undefined;
    const infinity = infinityIdOfView(id) ?? undefined;
    if (infinity) {
      const def = INFINITY_DEFS[infinity];
      return { name: def.name, icon: def.icon, wall, infinity };
    }
    if (wall) {
      const def = WALL_DEFS[wall];
      return { name: def.name, icon: def.icon, wall, infinity };
    }
    if (builtin) return { name: builtin.name, icon: builtin.emoji ?? "", wall, infinity };
    // Saved / temp view: only trust the loaded document when it is the one we are on.
    if (view && view.id === id) return { name: view.name, icon: view.icon ?? "", wall, infinity };
    return null;
  }, [view, viewId]);
  const user = useSession((s) => s.user);
  // GOON ROOMS: the room socket — the people — opened here so the bar's picker knows who
  // may drive (the host) and the stage knows who is in it. Null off a room.
  const room = useRoom(roomId ?? null, !!user);
  const isHost = room?.role === "owner";
  // FOLLOW MODE. A room is the host's screen, mirrored: the HOST's DisplayApp runs the
  // real view id (a quick setup, a temp view, a wall, a scroll) exactly as /screen does
  // and mirrors what it has (useRoomMirror); a MEMBER's runs the room's own id (the hub's
  // snapshot arrives as `config`), its tiles are DRIVEN off the room's bus, and nothing
  // here may edit: no hover chrome, no feed popup, no picker, no pencil. What stays a
  // member's own: the crown, sound mode, master volume, the goon session, their pause.
  const serverOffset = useServerClockOffset(undefined, !!room && !isHost);
  const follow: DrivenBy | null = useMemo(
    () => (room && !isHost ? { bus: room.regionsBus, serverOffset } : null),
    [room, isHost, serverOffset],
  );
  const canEdit = !follow;
  // The host's pause holds every member; a member's own pause is theirs alone.
  const tilesPaused = paused || (!!follow && !!room?.paused);
  // The room says what this screen plays: the host's showing itself, a member the
  // room's id (a `t_` id is a write claim, and a member holds none of the host's).
  const showing = room?.showing ?? null;
  useEffect(() => {
    if (!room) return;
    setViewId(isHost && showing ? showing : roomViewId(room.roomId));
  }, [room, isHost, showing]);


  // Stable registry of live region controllers (filled by each RegionPlayer).
  const registryRef = useRef<RegionRegistry>(new Map());
  // Per-region "fap + burst" callbacks registered by each RegionFapOverlay, so a HOST can
  // route a gesture in. Nothing on this screen reads them: fapping is the crown's alone,
  // and the double tap belongs to the feed popup now (see the tap contract at the tile).
  const fapBusRef = useRef<FapBus>(new Map());
  const syncBusRef = useRef(new SyncBus());
  // THE HOST MIRRORS its view, its reveals and its pause to the room (useRoomMirror).
  useRoomMirror({ room, view, syncBus: syncBusRef.current, paused });
  // Somebody else's fap: the crown rises on that tile here, and ONLY the crown —
  // burstOnly never touches this viewer's library or sends anything back.
  useEffect(() => room?.onFap((regionId) => fapBusRef.current.get(regionId)?.({ burstOnly: true })), [room]);
  // Keep `view`/`paused` reachable from the WS command handler without
  // re-subscribing the socket on every state change.
  const viewRef = useRef<View | null>(null);
  const pausedRef = useRef(paused);
  const identifyTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const displayConnRef = useRef<DisplayConn | null>(null);
  // The wrapper's status callback, reachable from the socket handler without putting it
  // in the effect's deps — an inline lambda from a caller would otherwise tear the socket
  // down and rebuild it on every render of that caller.
  const onConnectedRef = useRef(onConnected);
  const onNoViewRef = useRef(onNoView);
  const onForkedRef = useRef(onForked);
  // Sound, reachable from the socket effect without re-keying it on every toggle.
  const mutedRef = useRef(muted);
  const soundModeRef = useRef(soundMode);
  const soloIdRef = useRef(soloId);
  /*
   * What a wire unmute restores. A boolean has one inverse; a four-mode enum has three,
   * so "unmute" has to mean something and this is the memory that makes it mean the last
   * thing this screen actually sounded like. Never holds "muted" — see soundModeFromWire,
   * which turns that case back into `all`, exactly what the boolean always did.
   */
  const lastAudibleRef = useRef<SoundMode>("all");
  viewRef.current = view;
  pausedRef.current = paused;
  onConnectedRef.current = onConnected;
  onNoViewRef.current = onNoView;
  onForkedRef.current = onForked;
  mutedRef.current = muted;
  soundModeRef.current = soundMode;
  soloIdRef.current = soloId;
  if (soundMode !== "muted") lastAudibleRef.current = soundMode;

  /*
   * WHAT this screen plays and for HOW LONG — by KIND, by NAME + `view_key` for the
   * built-ins (quick setups, walls, scrolls, Editor's Choice), and by a pseudonymous
   * `view_id` alone for anything a user made, whose name is their own words (see
   * lib/viewEvents.ts). Never which clip. `view_played` once when a resolved view starts
   * showing; `view_watched` {duration_sec} on a 5-minute heartbeat plus the remainder
   * when the view changes or the page goes away — a television runs for hours and a killed tab never runs its
   * cleanup, so the heartbeat is what makes long sessions count. Keyed on the resolved
   * view's identity, not the id alone: on a retarget the old config lingers until the new
   * room answers, and counting under the wrong name would be worse than a late start.
   */
  /*
   * COMMUNITY WALL MODE. The wall is a place, not a layout: its squares are bought,
   * so the glass swaps the editing chrome for a money badge and the feed popup stays
   * shut (it would offer a write the server refuses). Everything screen-local — the
   * master audio panel, fullscreen, the fap crown, the goon session — stays exactly
   * as on any other view.
   */
  const wallMode = viewId ? viewIdKind(viewId) === "wall" : false;
  /*
   * A KIND WITH A STAGE OF ITS OWN (see stages.tsx). Infinity Walls deals its own pages
   * under the same bar, picker, session and remote as every other view; what changes is
   * only what fills the glass — and, like the wall, that it cannot be edited.
   */
  // The id whose KIND picks the stage: in a room, what the host is showing (a member's
  // own viewId is the room's, whose kind has no stage; the scroll they follow is named
  // by the showing), else this screen's own.
  const stageOf = room ? (showing ?? "") : viewId;
  const Stage = stageOf ? STAGES[viewIdKind(stageOf)] : undefined;
  const stageMode = Stage !== undefined;
  // The Infinite Scroll ALONE — what is the scroll's (its guide, its auto-scroll row)
  // must not follow every stage.
  const infinityMode = !!stageOf && viewIdKind(stageOf) === "infinity";
  const thumbIdle = infinityMode ? scrollThumbIdle : screenThumbIdle;
  // Auto-scroll's wait (the Infinite Scroll alone): the viewer's taste, per browser
  // (lib/infinityAutoScroll). Read once; the ☰ row writes it.
  const [autoScrollSec, setAutoScrollSecState] = useState<InfinityAutoScrollSec>(() => readInfinityAutoScroll());
  const setAutoScrollSec = (sec: InfinityAutoScrollSec) => {
    writeInfinityAutoScroll(sec);
    setAutoScrollSecState(sec);
    track("infinity_auto_scroll", { scroll: stageOf ? (infinityIdOfView(stageOf) ?? "random") : "random", sec });
  };
  // Keyed on the view id: the hook reads WHICH wall off it, and the two price apart.
  const wall = useWallState(wallMode && viewId ? viewId : null);

  /*
   * THE DAILY WALL ALLOWANCE (2026-09-10). Basic gets so many wall-minutes a UTC day —
   * minutes × tiles playing — and when they are spent this screen shrinks to a few
   * tiles with sound off until midnight, never stopping. The meter is the screen's to
   * keep (lib/wallAllowance.ts: the screen counts, the ledger corrects); this is where
   * it is driven and enforced. Off entirely for Gold, while the caps are dark, on a
   * wall view (the owner's rule: the Community and Premium Walls never count) and on a
   * stage (Infinity deals its own pages and cannot be shrunk — it does not count
   * either, for now).
   */
  const gate = useGoldGate();
  const publicSettings = usePublicSettings();
  const keptTiles = publicSettings?.gold?.degradedRegions ?? DEFAULT_SETTINGS.gold.degradedRegions;
  const allowanceSeconds = gate.wallMinutesPerDay * 60;
  const usedSeconds = useWallAllowance((s) => (s.serverSeconds ?? 0) + s.localSeconds);
  // A member's tiles are driven — they run no feed and spend no allowance.
  const meterOn = Number.isFinite(allowanceSeconds) && !wallMode && !stageMode && canEdit;
  const exhausted = meterOn && usedSeconds >= allowanceSeconds;
  /** What this screen draws: the authored/transposed regions, or the spent screen's few. */
  const drawnRegions = useMemo(
    () => (shownRegions && exhausted ? degradeRegions(shownRegions, keptTiles) : shownRegions),
    [shownRegions, exhausted, keptTiles],
  );
  // The batcher tags every interval with the view kind at its start, and banks open
  // intervals across a pause — a paused wall is not watching (see watchReport.ts).
  useEffect(() => setWatchOnWall(wallMode), [wallMode]);
  useEffect(() => {
    if (paused) watchPause();
    else watchResume();
  }, [paused]);
  // Signed in, the ledger seeds the count; a sign-in mid-session re-seeds.
  useEffect(() => void seedWallAllowance(), [user?.id]);
  const tilesPlaying = tilesPaused || !meterOn ? 0 : (drawnRegions?.length ?? 0);
  useEffect(() => {
    if (tilesPlaying <= 0) return;
    const t = window.setInterval(() => useWallAllowance.getState().tick(tilesPlaying, 1, Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [tilesPlaying]);
  // The moment it runs out: the gate, once per screen per day, and the shape of the
  // wall that got shrunk (the tuning question's input).
  const exhaustedOn = useRef<string | null>(null);
  useEffect(() => {
    if (!exhausted) return;
    const day = utcDayOf(Date.now());
    if (exhaustedOn.current === day) return;
    exhaustedOn.current = day;
    trackGoldGate("allowance");
    track("wall_allowance_exhausted", {
      wallMinutes: gate.wallMinutesPerDay,
      tiles: shownRegions?.length ?? 0,
      signedIn: !!user,
    });
  }, [exhausted, gate.wallMinutesPerDay, shownRegions, user]);
  // A buy dialog left open across a retarget would poll a square the new board does not
  // have and sit on "Loading this square…" for good. Latent while there was one wall;
  // real now that the arrows step from one to the other.
  /*
   * The buy dialog's lifecycle against the view — ONE effect, because two would race.
   *
   * A buy dialog left open across a RETARGET (the arrows stepping from one wall to another)
   * would poll a square the new board does not have, so a real view change clears it —
   * tracked against a ref, not the mere fact that this effect ran, so StrictMode's
   * double-invoke does not clear a dialog it just opened.
   *
   * `?buy=<regionId>` opens the take-it-back card's lost square (there is no other deep-link
   * into a dialog). Applied ONCE per mount, only when THIS board is the region's wall (a
   * premium square never opens on the Community Wall), then scrubbed so a reload does not
   * reopen it. A signed-in /screen becomes a device and redirects; PendingScreen carries the
   * `?buy` across that redirect, so this fires on the device's board.
   */
  const buyApplied = useRef(false);
  const prevView = useRef(viewId);
  useEffect(() => {
    const viewChanged = prevView.current !== viewId;
    prevView.current = viewId;
    if (!buyApplied.current && wallMode && viewId) {
      const region = buyParams.get("buy");
      if (region && wallIdOfView(viewId) === wallIdOfRegion(region)) {
        buyApplied.current = true;
        setWallBuyRegionId(region);
        const next = new URLSearchParams(buyParams);
        next.delete("buy");
        setBuyParams(next, { replace: true, state: routeStateRef.current });
        return;
      }
    }
    if (viewChanged) setWallBuyRegionId(null);
  }, [viewId, buyParams, wallMode, setBuyParams]);
  /*
   * THE SURFACE GUIDE — one auto-open, whichever screen this is (see guides.tsx for why
   * each surface teaches itself). The wall gets the combined screen+marketplace card
   * (`wall`), every other view the plain screen card (`screen`); WallIntroDialog is
   * deleted — the wall is a screen before it is a marketplace, and it is the first
   * screen most visitors ever see, so it owed them the screen mechanics too.
   *
   * Opens on arrival, every SESSION until that surface's own "don't show this again" —
   * a "Got it" holds for the rest of the tab session (guideDismissed reads both flags) —
   * behind two gates:
   *
   *  - NOT OVER THE CODE CARD (`pairing?.shown`): somebody typed /screen to pair a
   *    television, and a guide landing on a code mid-pairing is the stacked-dialog bug
   *    this gate family exists to kill. In the deps, so closing the card lets the
   *    guide take its turn.
   *  - NOT ON A PHONE (BAR_CUTOFF_PX), where the dialog lands on top of the thing it
   *    is explaining — the ⓘ in the bar is the phone's door, one tap away at every
   *    width. Matching the bar's own cutoff rather than a pointer query, because the
   *    thing being avoided is the SIZE of the dialog against the viewport. An
   *    environment that answers neither falls through to not auto-opening.
   *
   * ONCE PER SURFACE PER PAGE LOAD, by ref: pressing next from the wall onto a quick
   * setup is ARRIVING somewhere new, and the new surface owes its own introduction —
   * a global once-per-load flag swallowed exactly that (found by a user doing exactly
   * that on `/`). Per surface and not per view, because a "Got it" (which deliberately
   * writes no flag) must not re-open the same card on every screen→screen cycle of one
   * sitting.
   *
   * The SURFACE IS CAPTURED at open time, not read live at render: the board's poll can
   * re-render underneath an open dialog and must not swap its subject. An EXPLICIT view
   * change is different — the effect re-runs on wallMode and replaces the open card with
   * the new surface's own, which is the introduction the press asked for.
   */
  const [guide, setGuide] = useState<{
    surface: "wall" | "screen" | "infinity";
    source: "first-run" | "info";
  } | null>(null);
  const guideOffered = useRef<Set<"wall" | "screen" | "infinity">>(new Set());
  // A member following a room gets no guide: nothing here is theirs to drive. The host
  // gets the surface's own, as on any screen.
  const guideSurface = !canEdit ? null : wallMode ? "wall" : infinityMode ? "infinity" : stageMode ? null : "screen";
  useEffect(() => {
    const surface = guideSurface;
    if (!surface || pairing?.shown || guideOffered.current.has(surface)) return;
    const roomForIt =
      typeof window !== "undefined" && window.matchMedia?.(`(min-width: ${BAR_CUTOFF_PX}px)`).matches;
    if (!roomForIt) return;
    if (guideDismissed(surface)) return;
    guideOffered.current.add(surface);
    setGuide({ surface, source: "first-run" });
  }, [guideSurface, pairing?.shown]);

  const viewName = view?.name ?? null;
  // The screen card's subject: the LIVE view's display name, and only once the loaded
  // view is the one asked for — a stale name from the previous view is worse than the
  // generic line (namedScreenGuide falls back to it on null).
  const shownViewName = view && view.id === viewId ? view.name : null;
  const resolvedViewId = view?.id ?? null;
  // Photos revealed since the last view_watched — counted at recordView, read and zeroed
  // at each flush, so `photos_shown` is the denominator a photo skip rate needs.
  const photosShownRef = useRef(0);
  useEffect(() => {
    const v = viewRef.current;
    if (!v || !viewId || v.id !== viewId) return;
    /*
     * WHICH view, said only in the site's own words (lib/viewEvents.ts): a built-in —
     * quick setup, wall, scroll, an Editor's Choice collection — keeps its `name`
     * byte-for-byte and adds a stable `view_key`; anything a user made sends a
     * pseudonymous `view_id` and no name at all. Built-in is decided by the id's KIND or
     * the regions' SOURCE, never by the name.
     */
    /*
     * Whether the regions have Adaptive Framing switched on. From the SETTING, not from
     * what is on screen: a view is either authored to steer its crops or it is not, and
     * that is the lever an experiment would pull once the catalog is fully swept.
     */
    const framedRegions = v.regions.filter(
      (r) => adaptiveFramingOn(r) && r.fit !== "contain",
    ).length;
    const framing =
      framedRegions === 0 ? "off" : framedRegions === v.regions.length ? "on" : "mixed";
    /*
     * And what share of the tiles were ACTUALLY being steered when the event fired. A clip
     * the sweep has not reached carries no focal track and falls back to centre-cover, so
     * this is the real exposure rather than the intent — and while the sweep is partway
     * through the catalog it is the only within-subject control this feature has.
     */
    const framedPct = () => {
      const live = [...registryRef.current.values()]
        .map((c) => c.current())
        .filter((i): i is MediaItem => !!i);
      if (!live.length) return null;
      return Math.round((100 * live.filter((i) => i.focal?.length).length) / live.length);
    };
    let props: (ViewPlayProps & { framing: typeof framing }) | null = null;
    let cancelled = false;
    let since = Date.now();
    const start = (ec: EditorsChoiceIndex | null) => {
      if (cancelled) return;
      props = { ...viewPlayProps(viewId, v, ec), framing };
      track("view_played", { ...props, framed_pct: framedPct() });
      since = Date.now();
      photosShownRef.current = 0;
    };
    // Only a view that could BE an Editor's Choice play waits for the catalog (one public
    // fetch a page, shared); everything else is known from its id and reports at once.
    if (editorsChoiceSourceOf(viewId, v)) {
      void loadEditorsChoice(() => api.listCollections()).then(start);
    } else {
      start(null);
    }
    const flush = () => {
      if (!props) return;
      const duration_sec = Math.round((Date.now() - since) / 1000);
      since = Date.now();
      // Under five seconds is a retarget in flight, not a watch.
      const photos_shown = photosShownRef.current;
      photosShownRef.current = 0;
      // Whether any tile holds photos for other than the default — from the view as it
      // stands NOW, since the Photo time chip edits it mid-watch.
      const photo_time_custom = (viewRef.current?.regions ?? []).some(
        (r) => regionPhotoSeconds(r) !== FULL_IMAGE_SECONDS,
      );
      if (duration_sec >= 5)
        track("view_watched", {
          ...props,
          duration_sec,
          framed_pct: framedPct(),
          photos_shown,
          photo_time_custom,
        });
    };
    const beat = setInterval(flush, 5 * 60_000);
    window.addEventListener("pagehide", flush);
    return () => {
      cancelled = true;
      clearInterval(beat);
      window.removeEventListener("pagehide", flush);
      flush();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewId, resolvedViewId, viewName]);

  const handleCommand = useCallback((cmd: DisplayCommand) => {
    switch (cmd.kind) {
      case "reload":
        location.reload();
        break;
      case "showing":
        // A retarget from the remote. Re-point in place — the socket effect rejoins the
        // new view's room on its own — rather than reloading, which would drop fullscreen.
        setViewId(cmd.viewId);
        break;
      case "pause":
        setPaused(true);
        break;
      case "resume":
        setPaused(false);
        break;
      case "mute":
        // THE REMOTE IS THE BLUNT INSTRUMENT, and deliberately so: it is held by somebody
        // who cannot see the channel row and can only mean "be quiet". The mode toggle at
        // the glass is the fine one. Routed through the same mapper the bar's button uses
        // so the two cannot end up disagreeing about what unmute restores.
        setSoundMode(soundModeFromWire(cmd.on, lastAudibleRef.current));
        break;
      case "identify":
        if (identifyTimer.current) clearTimeout(identifyTimer.current);
        if (cmd.on === false) {
          // Deselected on the remote: down at once, not at the hold window's end.
          setIdentifying(false);
          break;
        }
        setIdentifying(true);
        identifyTimer.current = setTimeout(() => setIdentifying(false), IDENTIFY_MS);
        break;
    }
  }, []);

  useEffect(() => {
    if (!viewId) return;
    let live = true;
    // Drop a mismatched document the moment the target changes so the stage cannot keep
    // rendering the previous mode's pages under a new viewId while hello is in flight
    // (paired with viewLabel's viewId fallback above).
    setView((cur) => (cur && cur.id === viewId ? cur : null));
    const conn = connectDisplay(viewId, {
      // The screen's doorstep is the ONE place every arriving view passes — hello
      // config, retarget rejoin, a live editor's relayout relayed verbatim over the
      // socket — so the audible-by-default stamp happens here, not on the server,
      // where GET/preview/fork would leak it into editor saves.
      onConfig: (v) => {
        if (live) setView(withSoundDefault(v));
      },
      onUpdate: (v) => {
        if (live) setView(withSoundDefault(v));
      },
      onCommand: (cmd) => {
        if (live) handleCommand(cmd);
      },
      onSession: (s) => {
        if (live) setSession(s);
      },
      onStatus: (c) => {
        // Ignore a superseded socket: connectDisplay already skips intentional-close
        // status, and this flag covers any late callback after cleanup.
        if (!live) return;
        setConnected(c);
        onConnectedRef.current?.(c);
      },
    }, { deviceId });
    displayConnRef.current = conn;
    // A fresh socket knows nothing about this screen's sound until told; say it now
    // rather than on the next beat, so a remote watching this device is never wrong for
    // a heartbeat's length after a retarget.
    conn.reportMuted(mutedRef.current);
    return () => {
      live = false;
      conn.close();
      displayConnRef.current = null;
      // This teardown is usually a retarget — whose follow-up identify-off gets dropped
      // mid-rejoin (the relay has no socket to hit) — and the clearTimeout below destroys
      // the hold window's expiry, the only other way down. Lower the light here; a
      // still-held selection re-lights it on the next pulse.
      if (identifyTimer.current) clearTimeout(identifyTimer.current);
      setIdentifying(false);
    };
  }, [viewId, deviceId, handleCommand]);

  /* The remote reads this screen's sound off presence, and presence is built from what the
     screen reports — so report it the moment it changes, not on the next beat. */
  useEffect(() => {
    displayConnRef.current?.reportMuted(muted);
  }, [muted]);

  /*
   * The heartbeat.
   *
   * Not cosmetic: the `ping` it sends is the server's only periodic signal from a
   * screen, and the server's handler for it marks the device seen (which is what
   * presence and the reaper read) AND keeps a watched temporary view alive. Losing this
   * loop would take every screen offline in the rail and expire the very view it is
   * showing.
   *
   * ONE heartbeat, not two (2026-09-10). This used to GET the HTTP ping route every beat to
   * measure latency and then send that number over the socket — a second round trip per
   * beat, 27 HTTP requests a minute per screen, for a figure the socket's own echo can
   * produce. The `pong` now measures it. And every 5 s rather than 2.5: a screen with a
   * live socket is live whatever its beat is doing (the cap and the reaper both check the
   * socket set first), the claimed-device grace is 15 s — three misses — and the session
   * idle rule reads a once-a-minute touch. Twice the interval, half the chatter, nothing
   * that was true before is false now.
   */
  useEffect(() => {
    displayConnRef.current?.beat();
    const timer = setInterval(() => displayConnRef.current?.beat(), PING_MS);
    return () => clearInterval(timer);
  }, []);

  /*
   * A socket that came up and was never handed a view.
   *
   * `resolveView` on the server answers a hello for an id it cannot find with a
   * `t:"error"` frame rather than closing the socket, and connectDisplay has no case for
   * that message — so it is dropped, `connected` stays true, and this component sits on
   * WaitingForConfig indefinitely. Nothing here can see the frame; what it can see is the
   * silence. Report it once and let the wrapper ask the server what is true.
   */
  useEffect(() => {
    if (!viewId || view) return;
    const t = setTimeout(() => onNoViewRef.current?.(), NO_CONFIG_MS);
    return () => clearTimeout(t);
  }, [viewId, view]);

  /*
   * Sessions belong to the operator, so there is no id to pass — and every screen can
   * start one now.
   *
   * There used to be a `sessionsEnabled = !ephemeralView` gate here, on the reasoning
   * that /play/random and a signed-out collection play are "watch something" rather than
   * "start a session", and that every browser has an anon identity so the gate was what
   * stopped a casual glance silently counting edges. The reasoning was sound; the
   * mechanism was not. It keyed on a PROP, so it did not describe an intent at all, it
   * described which of two code paths had built the view — with the result that the
   * button labelled "Start gooning" landed on the one screen where a session could not be
   * started.
   *
   * Nothing counts TOWARDS THE SESSION until the operator presses Start, which is the real
   * gate and always was: `recordView` still checks sessionActiveRef before the session
   * tally. The lifetime per-clip view count is a different number and is not gated — a
   * screen with no session running still reports its views, once per clip per page (see
   * lib/clipViews.ts).
   */
  /*
   * AS THE DEVICE'S OWNER, when this screen is a device. A television claimed from a
   * phone runs under an identity of its own that nobody's session belongs to; passing the
   * device id makes every one of these act for the owner the device belongs to — the
   * person whose remote is showing the timer. Without it, views played here tallied into
   * nothing and the Goon button here started a session no remote could see. A screen with
   * no device (the pending screen) is its own identity, as before.
   */
  const startSession = useCallback(() => {
    void api.session
      .start(deviceId)
      .then((r) => {
        setSession(r.session);
        // In a room, the room hears it — a grey line with my name.
        room?.sendAct("session_start");
      })
      .catch(() => undefined);
  }, [deviceId, room]);

  // The screen's own identity for the vault (2026-09-09): /source/fetch carries no
  // bearer, so the device id is how the server learns whose screen this is — and it
  // counts only when the device belongs to an account AND the view it shows carries
  // `uploads:<owner>` (see SourceFetchRequest.deviceId). Cleared on unmount so a later
  // anonymous surface in this tab inherits nothing.
  useEffect(() => {
    setSourceDevice(deviceId);
    return () => setSourceDevice(undefined);
  }, [deviceId]);
  const edgeSession = useCallback(() => {
    void api.session
      .edge(deviceId)
      .then((r) => {
        setSession(r.session);
        room?.sendAct("edge");
      })
      .catch(() => undefined);
  }, [deviceId, room]);
  /*
   * AN EDGE COUNTS FOR WHATEVER IS ON THE SCREEN. A press reaches here however it was made:
   * this screen's button answers with the session, a remote's arrives as a `session` frame.
   * Either way the edge times grow, so this one place names the clips, once per press. The
   * first sight of a session only takes note: a reload must not re-credit old edges.
   */
  const edgeMark = useRef<{ id: string; seen: Set<number> } | null>(null);
  useEffect(() => {
    if (!session) {
      edgeMark.current = null;
      return;
    }
    const mark = edgeMark.current;
    if (!mark || mark.id !== session.id) {
      edgeMark.current = { id: session.id, seen: new Set(session.edgeTimes) };
      return;
    }
    const fresh = session.edgeTimes.filter((t) => !mark.seen.has(t));
    if (!fresh.length) return;
    for (const t of fresh) mark.seen.add(t);
    const showing = new Map<string, MediaItem>();
    for (const c of registryRef.current.values()) {
      const item = c.current();
      if (item && (c.onGlass?.() ?? true)) showing.set(item.id, item);
    }
    if (!showing.size) return;
    for (const t of fresh) void api.session.creditEdge(t, [...showing.values()], deviceId).catch(() => undefined);
  }, [session, deviceId]);

  const [confirmingStop, setConfirmingStop] = useState(false);
  const sessionRunning = session !== null;
  useEffect(() => setWatchSession(sessionRunning, deviceId), [sessionRunning, deviceId]);
  const stopSession = useCallback(() => {
    // The recap is read right after the stop, so the seconds go first. Not for long: a
    // slow link must not hold up a person pressing Stop.
    void Promise.race([flushWatchSession(), new Promise((r) => setTimeout(r, 1500))])
      .then(() => api.session.stop(deviceId))
      .then((r) => {
        setSession(null);
        if (r.session) setSummary({ session: r.session }); // the stats popup, on this device
        if (room && r.session) room.sendAct("session_end", Math.max(1, Math.round((Date.now() - r.session.startedAt) / 60_000)));
      })
      .catch(() => undefined);
  }, [deviceId, room]);

  /*
   * PLAY 1'S PICK: which panel of the wall carries the sound right now.
   *
   * Re-evaluated rather than tracked. The registry already knows what every tile is
   * showing, so the whole wall is re-read from it and pickSolo decides — which means this
   * needs no per-region bookkeeping and cannot drift out of step with what is on screen.
   *
   * pickSolo is sticky, so the common case (a panel swapping one video for another) is a
   * no-op, and setSoloId is guarded on identity so a re-pick that lands on the same panel
   * costs no render. `audible()` is the registry's own answer — a video whose MEASURED
   * audio is not `false`; see RegionController.
   *
   * A no-op outside Play 1: the other three modes do not consult a pick, and running the
   * picker in them would churn state nothing reads.
   */
  const repick = useCallback(() => {
    if (soundModeRef.current !== "one") return;
    const order = audibleCandidates(viewRef.current?.regions ?? []).map((r) => r.id);
    const next = pickSolo(
      order,
      (id) => registryRef.current.get(id)?.audible() ?? false,
      soloIdRef.current,
    );
    setSoloId((prev) => (prev === next ? prev : next));
    const sounding = next !== null && (registryRef.current.get(next)?.audible() ?? false);
    setSoloAudible((prev) => (prev === sounding ? prev : sounding));
  }, []);

  const recordView = useCallback((item: MediaItem) => {
    // The lifetime view count: every real player surface reports it, session or no
    // session, and lib/clipViews.ts dedups it to once per clip per page. A SyncedRegion
    // follower fires no onView, so a synced pair is one view, not two.
    reportClipView(item);
    if (item.kind === "image") photosShownRef.current += 1;
    /*
     * THE PICK'S PRIMARY TRIGGER, and it costs nothing to add here. A region reveals a
     * clip, writes it to its visible-item ref and fires this once the clip has played a
     * moment — so by the time we run, the registry already reports the new answer for the
     * tile that changed.
     *
     * Note what is deliberately not the trigger: the clip ENDING. A region bridges an
     * ended clip by replaying it until its successor has buffered, so "the audio ended"
     * is never a state the wall sits in, and a trigger built on it would fire against a
     * clip that is still audibly playing.
     */
    repick();
    // The SESSION tally is the thing gated on a session RUNNING.
    if (!sessionActiveRef.current) return;
    // APPLY THE RESPONSE. The server sends the updated session back on every recorded
    // view, and this used to drop it on the floor — which froze the bar's view count on
    // /screen, where the socket is an anonymous viewer (no deviceId, so no owner room,
    // so no `session` frames; see ws.ts). A device-joined screen gets the same state
    // again over the socket a beat later, which is a harmless overwrite, not a conflict.
    void api.session
      .view(item, deviceId)
      .then((r) => setSession(r.session))
      .catch(() => undefined);
  }, [deviceId, repick]);

  /*
   * The pick's other three triggers.
   *
   * Entering Play 1 needs one because nothing has been chosen yet; a VIEW change needs one
   * because a relayout can add or remove eligibility under a standing pick. The sweep
   * covers the case a push cannot: a region whose source errored will never reveal again,
   * so it will never call recordView, and without a clock the wall would hold a pick that
   * has gone permanently quiet. Five seconds is slow enough to be free and quick enough
   * that nobody watches silence wondering.
   */
  useEffect(() => {
    repick();
  }, [soundMode, view, repick]);
  useEffect(() => {
    if (soundMode !== "one") return;
    const t = setInterval(repick, 5_000);
    return () => clearInterval(t);
  }, [soundMode, repick]);

  /*
   * The pointer's override of the standing pick.
   *
   * DEBOUNCED ON THE WAY IN, INSTANT ON THE WAY OUT, and separately from the ring. A
   * pointer resting on a tile boundary flaps between two regions; a flickering ring is
   * cosmetic, but flapping audio is an audible chirp, because every flip re-runs the
   * player's muted effect and calls play() on both crossfade elements. So the ring keeps
   * reading `hovered` directly and stays instant, and only the audio waits to be sure.
   *
   * Never written into soloId: the override is computed and discarded, so leaving the tile
   * returns sound to the standing pick. Writing it through would let a mouse swept across
   * the wall permanently re-aim the picker at whatever it passed over last.
   */
  const [hoverAudioId, setHoverAudioId] = useState<ID | null>(null);
  useEffect(() => {
    const target = hoverSolo(view?.regions ?? [], hovered);
    if (target === null) {
      setHoverAudioId(null);
      return;
    }
    const t = setTimeout(() => setHoverAudioId(target), 200);
    return () => clearTimeout(t);
  }, [hovered, view]);

  /** The panel Play 1 is sounding: the pointer's override, else the standing pick. Null in
   *  every other mode, which is what makes soundsHere ignore it there. */
  const audibleId = soundMode === "one" ? (hoverAudioId ?? soloId) : null;

  /*
   * THE MASTER AUDIO PANEL'S OPEN/CLOSE — a hover on a mouse, a tap on a finger, and
   * focus on a keyboard, each wanting different treatment and only the event knowing
   * which happened.
   *
   * A MOUSE opens on intent (a short delay, so crossing the button on the way somewhere
   * else does not flash a panel) and closes on a delay after leaving, because a pointer
   * travelling from the button down into the panel is briefly over the gap between them.
   * Both delays are cheap insurance against flicker, and both are cancelled by the other
   * gesture arriving.
   *
   * A FINGER fires enter and leave inside one tap and can hover nothing, so it opens at
   * once and its leave is ignored — an outside tap or Escape dismisses it, which is the
   * panel's own job. A KEYBOARD opens and closes immediately: focus either is on the
   * group or is not, with no notion of passing by.
   *
   * Opening is never the CLICK's job. The click still toggles the sound, exactly as it
   * always did, and a panel that opened and closed on that same click could never be
   * dismissed without also changing what the room hears.
   */
  const [audioOpen, setAudioOpen] = useState(false);
  /* The radio. Called HERE and not in the panel: the panel unmounts every time the
     dropdown closes, and audio living in its tree would stop the moment somebody looked
     away from it. */
  const radio = useRadio({ channels: CHANNELS, master });
  /*
   * THE MODE DRIVES THE TRANSPORT, but only at its EDGES. The channel is this screen's
   * soundtrack: stepping into `radio` or `all` presses play — choosing `radio` obviously
   * asks for it, and unmuting into `all` brings it along with the panels, by request,
   * rather than leaving a mode half-silent behind a second button nobody was told to
   * find. `muted` presses pause, radio included: before this effect the channel sang
   * straight through the bar's red button, which made "Muted — nothing plays" a lie.
   * `one` is the BED's — the effect below — because there the answer depends on whether
   * a panel is speaking, not on the mode alone. radioAudible is the shared statement of
   * who may be heard. Edges only, via the ref: INSIDE a mode the transport stays
   * hand-owned, and a pause pressed there must not be overridden on the next render.
   * play() still lands within the click's activation window, and a mode restored by the
   * remote with no local gesture simply leaves the transport honest when the browser
   * refuses — the same bargain the hook's catch already keeps.
   */
  const soundModeWas = useRef(soundMode);
  useEffect(() => {
    const was = soundModeWas.current;
    if (was === soundMode) return;
    soundModeWas.current = soundMode;
    if (!radioAudible(soundMode)) radio.pause();
    // UNMUTING NEVER PRESSES PLAY (owner, 2026-10-29): the channel stays where its
    // transport was until someone presses play. Only CHOOSING Radio — a mode that is
    // nothing but the channel — asks for it.
    else if (soundMode === "radio" && !radio.playing) radio.play();
  }, [soundMode, radio]);
  /*
   * THE BED. In Play 1 the channel UNDERLAYS the wall: it sounds while no panel does and
   * steps aside the moment one actually speaks — a fade each way, never a cut, because
   * the whole point of Play 1 is that the room is listening. "Speaking" is the standing
   * pick's MEASURED noise (soloAudible — kept honest by reveal-triggered repicks and the
   * five-second sweep, so a panel that errors into silence hands back to the bed within
   * seconds) OR the pointer's hover override — aiming audio at a tile counts before the
   * tile proves it, or the bed would talk over the very panel being auditioned. Edge-
   * driven off its own ref exactly like the mode effect above: a hand press on the
   * transport holds until the wall actually changes its answer, and a render that
   * changes neither signal presses nothing. Nulled outside Play 1 so re-entry always
   * re-applies against the wall's current answer.
   */
  const bedWas = useRef<boolean | null>(null);
  /** The bed only ever RETURNS what it took: true while it has faded a channel the
   *  viewer had playing. It never starts one (owner, 2026-10-29: unmuting must leave the
   *  channel paused until play is pressed). */
  const bedDucked = useRef(false);
  useEffect(() => {
    if (soundMode !== "one") {
      bedWas.current = null;
      bedDucked.current = false;
      return;
    }
    const speaking = soloAudible || hoverAudioId !== null;
    if (bedWas.current === speaking) return;
    bedWas.current = speaking;
    if (speaking) {
      if (radio.playing) {
        bedDucked.current = true;
        radio.pause({ fade: true });
      }
    } else if (bedDucked.current) {
      bedDucked.current = false;
      radio.play({ fade: true });
    }
  }, [soundMode, soloAudible, hoverAudioId, radio]);
  /*
   * A channel started. Keyed on the id, and remembered — so a loop does not re-report
   * (the body repeats every eleven minutes and would be a heartbeat), and neither does
   * pausing and resuming the same channel. Lives HERE rather than in the panel because a
   * channel started while the dropdown is shut still counts.
   */
  const playedRef = useRef<string | null>(null);
  useEffect(() => {
    const id = radio.current?.id ?? null;
    if (!radio.playing || !radio.current || playedRef.current === id) return;
    playedRef.current = id;
    trackChannelPlayed(radio.current);
  }, [radio.playing, radio.current]);
  /* The partner credit, owned HERE and not by the panel. A dropdown that dismisses on
     outside pointer-down unmounts a modal it owns on that modal's first click — the trap
     AccountControl documents — so the row reports the press upward and the dialog is
     mounted at this level, above the panel's lifetime. */
  const [partner, setPartner] = useState<PartnerInfo | null>(null);
  const audioOpenTimer = useRef<number | null>(null);
  const audioCloseTimer = useRef<number | null>(null);
  const clearAudioTimers = useCallback(() => {
    if (audioOpenTimer.current !== null) window.clearTimeout(audioOpenTimer.current);
    if (audioCloseTimer.current !== null) window.clearTimeout(audioCloseTimer.current);
    audioOpenTimer.current = null;
    audioCloseTimer.current = null;
  }, []);
  useEffect(() => clearAudioTimers, [clearAudioTimers]);

  const openAudio = useCallback(
    (kind: "mouse" | "touch" | "keyboard") => {
      clearAudioTimers();
      // Counted on the OPEN TRANSITION only — a pointer resting on the button would
      // otherwise report every time it re-entered.
      const open = () =>
        setAudioOpen((was) => {
          if (!was) trackAudioPanel();
          return true;
        });
      if (kind === "mouse") {
        audioOpenTimer.current = window.setTimeout(open, 150);
      } else {
        open();
      }
    },
    [clearAudioTimers],
  );
  const closeAudio = useCallback(
    (kind: "mouse" | "touch" | "keyboard") => {
      clearAudioTimers();
      if (kind === "mouse") {
        audioCloseTimer.current = window.setTimeout(() => setAudioOpen(false), 300);
      } else if (kind === "keyboard") {
        setAudioOpen(false);
      }
      // touch: a tap's own leave must not undo the tap.
    },
    [clearAudioTimers],
  );

  /** Play 1's pool: the panels the layout has left audible. Zero of them is silence, and
   *  the panel says so rather than letting somebody hunt for a fault. */
  const eligibleCount = audibleCandidates(view?.regions ?? []).length;

  /**
   * SOMETHING IS UP, so a tap on the glass belongs to that overlay and to nothing else.
   *
   * The master audio panel closes itself on a DOCUMENT-level pointerdown, and React
   * delegates from the root container — a descendant of document — so the panel's
   * listener runs AFTER the tile's, and the one tap that shut the panel also selected the
   * tile beneath it and, a beat later, opened its feed popup. Read at pointerdown for the
   * same reason `hovered` is: by click time the panel has already closed and the evidence
   * that a tap was ever a dismissal is gone.
   *
   * The sheets each paint a real full-bleed backdrop, so their dismissing tap never lands
   * on a tile at all. They are listed anyway, so this stays a rule about open overlays
   * rather than a patch for one panel — whatever dismisses itself the audio panel's way
   * next is covered the day it arrives.
   */
  const overlayOpen =
    audioOpen || picker || settingsOpen || summary !== null || partner !== null ||
    feedEditRegionId !== null;

  // Apply a live edit to one region: update the on-screen player immediately, then persist
  // it to the view with a PUT, so a reload of this screen still shows it. The PUT does not
  // push a ws `update` into the view's room, so another screen on the same view picks the
  // edit up on its next `config` — a reconnect or reload — not live.
  /**
   * Take a copy of what is playing and point this screen at it.
   *
   * The only way to "edit" a built-in: they are the same for everybody, so the copy
   * is what becomes yours. It is TEMPORARY on purpose — it expires once nothing is
   * watching, which is what tweaking what is on the telly actually means. Keeping it is
   * what the library's Copy is for.
   *
   * TWO WAYS TO LAND THE COPY, because there are two kinds of panel. A screen with a
   * device points the device at the copy; a socket is joined to its room at connect time
   * and nothing moves it between rooms, so that only takes effect on a fresh load — hence
   * the reload, the same nudge a controller's reassignment sends. An anonymous panel (the
   * pending screen, playing a quick setup while it waits to be claimed) has no device row
   * at all, so it swaps by prop and needs no reload.
   *
   * Returns the copy rather than navigating: where the caller goes next (the editor, in
   * this tab) is its business, and it needs the id to get there.
   */
  const forkOntoThisScreen = useCallback(async (): Promise<View | null> => {
    const current = viewRef.current;
    if (!current) return null;
    // The server copies the regions off the id itself — the fork endpoint is the one
    // door a 16-region built-in walks through the region caps, precisely because
    // nothing in this request carries regions to trust.
    const { view: copy } = await api.views.forkFrom(current.id);
    track("view_created", { surface: "wall_fork" });
    if (deviceId) await api.devices.setShowing(deviceId, copy.id);
    else onForkedRef.current?.(copy.id);
    return copy;
  }, [deviceId]);

  /** Play the copy that was just taken, whichever kind of panel this is. A device screen
   *  re-points itself here (the write already happened in forkOntoThisScreen); an
   *  anonymous panel has already been handed the id by its caller through `onForked`. */
  const showForkedCopy = useCallback(
    (copy: View) => {
      // In a room the copy is the ROOM's to show: the host points the room at it and
      // hears the new showing back (room_showing) as its own viewId.
      if (room && isHost)
        void userApi
          .roomSetView(room.roomId, copy.id)
          .then(() => track("room_pointed", { kind: "temp", from: "fork" }))
          .catch(() => undefined);
      else setViewId(copy.id);
    },
    [room, isHost],
  );

  /**
   * The bar's Edit button, on EVERY screen — including a quick setup.
   *
   * It used to be a link, gated on `isViewWritable`, so the built-ins showed no way to
   * change what they were doing. Those are the views most likely to be on a television:
   * /screen plays one by default, and clicking a quick setup goes straight there. The
   * button that would have fixed it was the one thing missing from the one place it was
   * needed.
   *
   * So it does the copy-and-edit thing the built-in notice already offered — a temporary
   * copy, onto this screen, opened in the editor — and on a view that is already writable
   * it just opens the editor.
   *
   * IN THIS TAB. It used to claim a new tab on every press, deliberately, so that the
   * screen being watched kept playing while it was edited. In practice one press was one
   * more tab to close, and a phone's browser eventually decided the site was a popup
   * spammer and refused the tab outright — at which point the pencil silently did
   * nothing, because nothing here ever read `blocked`. The case the new tab was for —
   * building on a laptop while the television follows live — is the library on a second
   * device, and that still works. On one device, editing what you are looking at means
   * going to the editor and coming back.
   *
   * THE WAY BACK is history: the editor is pushed on top of this screen with `fromScreen`
   * so it can offer "Back to screen", and the browser's own Back does the same. What is
   * found on return is the copy — a device was pointed at it by forkOntoThisScreen, and
   * the anonymous panel writes it into its own URL (see PendingScreen's onForked) before
   * this navigation is pushed, so the entry underneath is already the copy's.
   */
  const navigate = useNavigate();
  const editThisView = useCallback(() => {
    const current = viewRef.current;
    if (!current) return;
    // A room must not navigate away — its socket and the host's mirror die with the page
    // and the room with them — so there the editor opens BESIDE it; its saves reach this
    // screen as `update` frames, and the mirror carries them on to the room.
    const go = (id: string) =>
      room
        ? void window.open(`/views/${encodeURIComponent(id)}`, "_blank", "noopener")
        : navigate(`/views/${encodeURIComponent(id)}`, { state: { fromScreen: true } });
    if (isViewWritable(current.id)) {
      go(current.id);
      return;
    }
    setForking(true);
    void forkOntoThisScreen()
      .then((copy) => {
        if (!copy) return;
        showForkedCopy(copy);
        go(copy.id);
      })
      .catch(() => {})
      .finally(() => setForking(false));
  }, [forkOntoThisScreen, showForkedCopy, navigate, room]);

  /** PUT whatever the ref holds right now. Reading at fire time is what makes the
   *  debounce below safe: every edit folded in while the timer ran is in the body. */
  const persistTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const persistView = useCallback(() => {
    const next = viewRef.current;
    if (!next) return;
    void api.views
      .update(
        next.id,
        {
          name: next.name,
          icon: next.icon,
          aspect: next.aspect,
          // Hand-built body: omitting this here would blank the flag on every
          // wall-side region edit (parseInput reads only what arrives).
          ...(next.adaptiveLayout ? { adaptiveLayout: true } : {}),
          regions: next.regions,
        },
        "wall",
      )
      // Keep the optimistic state; it reconciles on the next config. But COUNT the
      // refusal — a builtin's session-local edit, an expired temp — because "the
      // control worked and the save didn't" is invisible from every other number.
      .catch(() => track("wall_edit_failed"));
  }, []);
  // A pending debounced persist must not outlive the component; the edit it carries is
  // already on screen, and a PUT firing into a torn-down context helps nobody.
  useEffect(() => () => {
    if (persistTimer.current) clearTimeout(persistTimer.current);
  }, []);

  /**
   * Reshape the regions and persist — the wall's one write path. The hover chrome's
   * link/unlink hands in the shared transforms (linkRegions/unlinkAdoptShared), so a
   * sync made on the glass is the same mutation the editor makes.
   *
   * `debounce` delays only the PUT, never the screen: the volume slider folds a patch
   * per pointer move and one request per settled drag is plenty. An undebounced edit
   * landing mid-timer flushes early — the fold already carries the slider's latest.
   */
  const applyRegionsEdit = useCallback(
    (transform: (regions: Region[]) => Region[], opts?: { debounce?: number }) => {
      const patchView = (v: View): View => ({ ...v, regions: transform(v.regions) });
      const current = viewRef.current;
      if (!current) return;
      // Fold the change into the ref as well as the state. Two edits landing in one tick
      // (key-repeat on the loop toggle across two hovered regions) both read this ref
      // before React re-renders, so without the write-back the second would rebuild from
      // the pre-edit snapshot and silently drop the first.
      const next = patchView(current);
      viewRef.current = next;
      setView((cur) => (cur ? patchView(cur) : next));
      if (persistTimer.current) clearTimeout(persistTimer.current);
      if (opts?.debounce) {
        persistTimer.current = setTimeout(() => {
          persistTimer.current = undefined;
          persistView();
        }, opts.debounce);
      } else {
        persistTimer.current = undefined;
        persistView();
      }
    },
    [persistView],
  );

  const applyRegionEdit = useCallback(
    (
      regionId: ID,
      patch: Partial<Pick<Region, "source" | "autoOptimize" | "soundOn" | "volume" | "photoSeconds">>,
      opts?: { debounce?: number },
    ) => {
      applyRegionsEdit(
        (regions) => regions.map((r) => (r.id === regionId ? { ...r, ...patch } : r)),
        opts,
      );
    },
    [applyRegionsEdit],
  );

  /** The region whose PLAYBACK a tile shows: its leader when synced, itself otherwise.
   *  A follower has no clip selection, buffer or registry entry of its own, so every
   *  playback affordance aimed at it can only mean the leader. */
  const playbackRegionId = (id: ID): ID => {
    const regions = viewRef.current?.regions ?? [];
    const r = regions.find((x) => x.id === id);
    return (r && syncLeaderOf(r, regions))?.id ?? id;
  };

  // Lock the region to the clip currently on screen (or restore its prior source).
  // Through a follower it locks the LEADER — the one region that holds both tiles.
  const toggleRepeat = useCallback(
    (regionId: ID) => {
      const targetId = playbackRegionId(regionId);
      const region = viewRef.current?.regions.find((r) => r.id === targetId);
      if (!region) return;
      if (region.source.kind === "clip") {
        const prev: SourceConfig = prevSourceRef.current.get(targetId) ?? { kind: "random" };
        track("region_loop_set", { on: false });
        applyRegionEdit(targetId, { source: prev });
      } else {
        const cur = registryRef.current.get(targetId)?.current();
        if (!cur) return;
        prevSourceRef.current.set(targetId, region.source);
        track("region_loop_set", { on: true });
        // The ON press only: releasing the loop is not a second loop. Fire-and-forget and
        // signed-in-only — userApi 401s for a signed-out screen, which is the same silence
        // the fap crown keeps rather than nagging someone who just wanted a clip to repeat.
        userApi.recordLoop(cur).catch(() => undefined);
        applyRegionEdit(targetId, { source: { kind: "clip", clip: cur } });
      }
    },
    [applyRegionEdit],
  );

  /**
   * Point THIS screen at a view — the picker's act and the arrows' act, shared.
   *
   * A real device records the assignment first, so a later resolve — a reload, a
   * second screen joining, the rail redrawing — reads what is actually on the
   * glass. A claimable screen with no device row (the pending screen, playing a
   * quick setup while it waits) has nothing to record, and tells its caller
   * instead so the caller's prop follows. Then the swap itself, the same way for
   * both: in place, no reload — reloading is the only reason a fullscreen TV ever
   * fell out of fullscreen when somebody changed what it was playing.
   */
  const pointTo = useCallback(
    async (id: string) => {
      // In a room, "what plays" is the ROOM's, not this screen's: the host sets it on the
      // server and hears the new showing back (room_showing → viewId); a member's pick
      // does nothing. A pick the room refuses is SAID, as a grey line in the chat.
      if (room) {
        if (!isHost) return;
        await userApi
          .roomSetView(room.roomId, id)
          .then(() => track("room_pointed", { kind: viewIdKind(id), scroll: infinityIdOfView(id) ?? undefined, from: "picker" }))
          .catch(() => {
            room.note("That pick didn't take.");
          });
        return;
      }
      // The write can FAIL and the swap must not: a TV claimed from a phone is owned
      // by the phone's account, not by the browser standing in the room, so this
      // screen's own setShowing answers 403 (the server says so itself — see
      // ensureShowing in routes/devices.ts). The person at the glass still gets
      // obeyed: swap in place and let the device row stay stale until an owner
      // retargets. Dead arrows on the screen would be the worse lie — and the poll
      // loop cannot snap this back, because it stops once something is playing.
      if (deviceId) await api.devices.setShowing(deviceId, id).catch(() => undefined);
      else onForkedRef.current?.(id);
      // Clear a mismatched document in the same turn as the id swap so the pill cannot
      // paint the previous mode for a frame (effect below also clears; this is the sync half).
      setView((cur) => (cur && cur.id === id ? cur : null));
      setViewId(id);
    },
    [deviceId, room, isHost],
  );

  /*
   * Left/Right cycle what's playing, in the What's Playing sheet's own order, wrapping
   * at both ends. The sheet and the arrows read ONE list (useWhatsPlayingEntries) so
   * they can never drift into offering different things.
   *
   * Finding "where are we" is the crooked part: views and quick setups match viewId
   * directly, but a collection landing mints a FRESH temp view, so its id belongs to no
   * row — mintedFromRef remembers which collection a mint came from (fed by both the
   * sheet's picks and the arrows' landings), and cursorRef remembers the last slot as
   * the fallback when a remote retarget or a fork hands us an id nobody can place.
   * Last write wins there, deliberately.
   */
  const { entries: playableEntries } = useWhatsPlayingEntries();
  const entriesRef = useRef(playableEntries);
  entriesRef.current = playableEntries;
  const viewIdRef = useRef(viewId);
  viewIdRef.current = viewId;
  const mintedFromRef = useRef(new Map<string, string>());
  const cursorRef = useRef(-1);
  // One retarget in flight: key-repeat must not stack setShowing round trips, and a
  // collection landing mints a real temp view per press — serialize, don't stack.
  const cycleBusyRef = useRef(false);

  const cycleShowing = useCallback(
    (dir: 1 | -1) => {
      const list = entriesRef.current;
      if (list.length === 0 || cycleBusyRef.current) return;
      const cur = viewIdRef.current;
      const from = cur
        ? list.findIndex((e) =>
            e.kind === "collection" ? mintedFromRef.current.get(cur) === e.id : e.id === cur,
          )
        : -1;
      const at = from >= 0 ? from : cursorRef.current;
      const next =
        at < 0 ? (dir === 1 ? 0 : list.length - 1) : (at + dir + list.length) % list.length;
      const entry = list[next];
      cursorRef.current = next;
      cycleBusyRef.current = true;
      const done = () => {
        cycleBusyRef.current = false;
      };
      if (entry.kind === "collection") {
        void pointCollectionEntry(entry, pointTo, (id, meta) =>
          mintedFromRef.current.set(id, meta.collectionId),
        ).then(done, done);
      } else {
        // The same act the sheet's tap records, arrived at by a different finger.
        track("whats_playing_picked", { kind: entry.kind });
        void pointTo(entry.id).then(done, done);
      }
    },
    [pointTo],
  );
  const cycleShowingRef = useRef(cycleShowing);
  cycleShowingRef.current = cycleShowing;

  /*
   * THE PHONE'S ARROWS ARE A FLICK. Left and Right on a keyboard step through what's
   * playing (below); a finger has no keys, and the arrows in the thumb bar fold away when
   * the glass goes idle. A sideways flick anywhere on the glass is the same act — the
   * same cycle, the same list, the same event — and it works on every view, the scrolls
   * included (their own scroll is vertical; see the hook for how the two axes share a
   * finger). Same gates as the keys: not under an open sheet or dialog.
   */
  /** The glass, measured for the chip's shadow (chipCovers): a tile in the top row under
   *  the Now Showing chip gets its hover chrome pushed down by the chip's row. */
  const glassRef = useRef<HTMLDivElement | null>(null);
  // While a session runs its tab hangs under the chip (SessionTab), so the shadow grows.
  const chromeInset = (r: Region) =>
    chipCovers(r, glassRef.current?.clientWidth ?? (typeof window !== "undefined" ? window.innerWidth : 1280))
      ? CHIP_ROW_PX + (session != null ? SESSION_TAB_PX : 0)
      : 0;

  const swipe = useHorizontalSwipe((dir) => {
    if (pickerRef.current || settingsOpenRef.current || summaryRef.current || feedEditRef.current) return;
    cycleShowingRef.current(dir);
  });

  // Always-on: the arrows work wherever the pointer is. Only an open sheet or dialog
  // owns the keyboard instead — cycling views under a modal reads as a haunting.
  const pickerRef = useRef(picker);
  pickerRef.current = picker;
  const settingsOpenRef = useRef(settingsOpen);
  settingsOpenRef.current = settingsOpen;
  const summaryRef = useRef(summary);
  summaryRef.current = summary;
  const feedEditRef = useRef(feedEditRegionId);
  feedEditRef.current = feedEditRegionId;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      // Alt/Ctrl/Cmd+arrow belongs to the browser (Back/Forward), same as the clip modal.
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      const el = e.target as HTMLElement | null;
      if (el && /^(INPUT|SELECT|TEXTAREA)$/.test(el.tagName)) return;
      if (
        pickerRef.current ||
        settingsOpenRef.current ||
        summaryRef.current ||
        feedEditRef.current
      )
        return;
      e.preventDefault();
      cycleShowingRef.current(e.key === "ArrowRight" ? 1 : -1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /*
   * Space skips to the next clip — the hovered tile alone when the pointer is on one,
   * the whole wall when it isn't. (It used to lock/repeat the hovered tile; that
   * gesture lives on the hover chrome's loop icon now, where it is visible instead of
   * secret.) The hovered id routes through playbackRegionId because a follower has no
   * registry entry — registry.get on its id would no-op silently — and skipping the
   * tile you point at can only mean its leader, which moves both tiles. Skip-all rides
   * the registry values() for the same reason from the other side: a SyncedRegion
   * never registers, so the values() ARE the regions that run feeds. Keyed off
   * `hovered` alone, not `chromeLock` — a locked chrome with the pointer off the tile
   * skips the wall, matching what the eye is over. Suppressed under the same modals as
   * the arrows — and on key-repeat, because each press should cost one skip, not one
   * per repeat beat (advance() itself also refuses while a swap is still in flight).
   */
  const hoveredRef = useRef(hovered);
  hoveredRef.current = hovered;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== " " && e.code !== "Space") return;
      if (e.repeat) return;
      const el = e.target as HTMLElement | null;
      if (el && /^(INPUT|SELECT|TEXTAREA)$/.test(el.tagName)) return;
      if (
        pickerRef.current ||
        settingsOpenRef.current ||
        summaryRef.current ||
        feedEditRef.current
      )
        return;
      e.preventDefault();
      const overId = hoveredRef.current;
      const targetId = overId ? playbackRegionId(overId) : null;
      const ctrls = targetId
        ? [registryRef.current.get(targetId)].filter(
            (c): c is RegionController => c !== undefined,
          )
        : [...registryRef.current.values()];
      // The kind skipped AWAY from, read before the advance moves it: photo skips are
      // the Photo time chip's success metric.
      const kind = ctrls.length === 1 ? (ctrls[0]!.current()?.kind ?? null) : null;
      for (const ctrl of ctrls) ctrl.advance();
      // Which shape moved — a layout fact, never a clip. Rate-limited upstream.
      if (targetId) track("screen_skip_region", { kind });
      else track("screen_skip_all", { regions: registryRef.current.size });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // The feed popup names a region by id, and the view under it can be SWAPPED while it
  // is open — an editor pushes a relayout over the socket, a remote retargets the
  // screen. A popup stranded on an id the view no longer has would edit nothing;
  // close it and let the person click the region they can actually see.
  useEffect(() => {
    if (!feedEditRegionId) return;
    if (!view?.regions.some((r) => r.id === feedEditRegionId)) {
      setFeedEditRegionId(null);
    }
  }, [view, feedEditRegionId]);

  // The mobile menu's sign-in dialog is owned HERE, above the menu that opens it —
  // SiteMenu dismisses on any outside mousedown and the modal portals outside it.
  // See MenuAccountItems.
  /* Which door, not just whether — SiteChrome's fork. "faps" is the phone-only Earn
     row, which opens register mode with the Faps reason. */
  const [menuAuthOpen, setMenuAuthOpen] = useState<null | "login" | "faps">(null);
  const [menuGoldOpen, setMenuGoldOpen] = useState<GoldUpsellGate | null>(null);
  /** Bumped by Build a Slut's "Start over": keys the stage, so bumping remounts it clean. */
  const [stageEpoch, setStageEpoch] = useState(0);
  // The FAPS dialog, same ownership: the pill in the bar and the menu row report the
  // tap, the dialog lives here.
  const [fapsOpen, setFapsOpen] = useState<FapsOpenSource | null>(null);
  // Watch-time FAPS cross their daily threshold on the server's own clock, off
  // watch_events rather than any button here — so the pill re-polls every five minutes
  // to catch the crossing without a page reload. The only interval the wallet ever runs on.
  const fapsEnabled = useFapsEnabled();
  const refreshFaps = useFapsStore((s) => s.refresh);
  useEffect(() => {
    if (!session || !fapsEnabled) return;
    const t = setInterval(() => void refreshFaps(), 5 * 60_000);
    return () => clearInterval(t);
  }, [session, fapsEnabled, refreshFaps]);

  // The "watch_view" quest ("watch today's pick for N minutes") has no other server
  // signal to read — unlike watch-time earning, which sums watch_events, the QUEST needs
  // to know it was THIS surface, not just any surface, that stayed open. So it's bumped
  // from here, once a minute, client-verified exactly like scroll_pages: the server
  // still checks the reported viewId against its own pickWatchTarget pick and no-ops on
  // a mismatch, so this is a report, not a grant.
  const watchViewTarget = useFapsStore((s) => s.wallet?.quests.watchTarget?.viewId);
  useEffect(() => {
    if (!session || !fapsEnabled || !viewId || !watchViewTarget || viewId !== watchViewTarget) return;
    const bump = () => void userApi.fapsQuestBump("watch_view", viewId).catch(() => undefined);
    bump();
    const t = setInterval(bump, 60_000);
    return () => clearInterval(t);
  }, [session, fapsEnabled, viewId, watchViewTarget]);

  /**
   * The controls that FOLD out of the bar on a phone, built once and rendered twice.
   *
   * `ScreenControls` draws the bar's glyph row and `ScreenThumbBar` the glass's chip and
   * running session — at every width, since 2026-09-09. One object rather than two prop lists
   * because the pairing is the whole contract — a control may fold from the bar only
   * because the thumb bar catches it, and two independent lists is precisely how the
   * phone lost Edit, the gear and the ⓘ in the first place. Add a control here and both
   * widths get it; there is no way to give it to only one.
   *
   * Sound and fullscreen are NOT in here, and that is not an oversight: neither folds, so
   * neither needs catching. See ScreenThumbBar's header.
   */
  /** The allowance gauge, built once: the thumb bar shows it filling, the glass keeps it spent. */
  const allowanceGauge = meterOn ? (
    <WallAllowanceGauge
      usedSeconds={usedSeconds}
      totalSeconds={allowanceSeconds}
      tilesPlaying={tilesPlaying}
      exhausted={exhausted}
      keptTiles={keptTiles}
      onGoPremium={() => setMenuGoldOpen("allowance")}
    />
  ) : null;
  const foldingControls = {
    label: viewLabel,
    // Every screen playing a view can change what it plays from here, the same two ways
    // forking already lands: a device retargets itself (setShowing + reload), a claimable
    // screen with no device swaps the view it plays in place. Only a screen with nothing
    // playing (no label) has no picker to offer, and both renderings drop the bubble there.
    // In a room only the HOST picks; a member's bar carries no picker and no arrows.
    onLabelPress: canEdit ? () => setPicker(true) : null,
    // Every view has something to say for itself: the wall gets the combined
    // screen+marketplace guide, everything else the plain screen one. The prop was
    // built general for exactly this one-liner.
    onInfo: guideSurface ? () => setGuide({ surface: guideSurface, source: "info" }) : undefined,
    onPrev: canEdit ? () => cycleShowing(-1) : null,
    onNext: canEdit ? () => cycleShowing(1) : null,
    // No edited version of the wall exists, not even a forked snapshot — the wall is a
    // place, and the pencil would only mint a private copy that stops being the wall the
    // moment a square sells.
    // Nor of Infinity: its pages are dealt, and a copy of one would be a copy of a
    // moment. Same slot in the bar, same absence.
    // Nor for a member of a room (nothing is theirs to edit), nor a host with nothing
    // showing yet (the pencil would fork the room's empty stub).
    onEdit: wallMode || stageMode || !canEdit || (room && !showing) ? null : editThisView,
    editBusy: forking,
    // No party state means this surface is not a claimable screen, so there is nothing
    // for a gear to open and both renderings drop it.
    onSettings: party ? () => setSettingsOpen(true) : null,
    // The running session rides the chip as a tab (ScreenThumbBar's SessionTab); its
    // start and stop are the bar's record dot (ScreenControls, via sessionActive).
    session,
    onEdge: edgeSession,
    // Read by ScreenControls: the record dot is the start idle and the stop running.
    sessionActive: session != null,
    // The Infinite Scroll's auto-scroll, a ☰ row (ScreenMenuItems) and nothing on the
    // glass (owner, 2026-09-14). Null off the scroll, so no wall or view grows the row.
    // A room's member follows the host's page: no clock of their own.
    autoScroll: infinityMode && canEdit ? { sec: autoScrollSec, onChange: setAutoScrollSec } : null,
    // "Start over" on the picking scrolls: a remount is the whole reset — pages, memory,
    // picks — and on Goonette of the Hill the saved game goes with it.
    onStartOver:
      canEdit && stageOf && (infinityIdOfView(stageOf) === "build" || infinityIdOfView(stageOf) === "battle")
        ? () => {
            track("infinity_start_over", { scroll: infinityIdOfView(stageOf) });
            if (infinityIdOfView(stageOf) === "battle" && user) void api.battleReset().catch(() => undefined);
            setStageEpoch((e) => e + 1);
          }
        : null,
    // The sheet renders its own plain row for starting one; see ScreenThumbBar.
    onStartSession: startSession,
    // Asks first: it sits right beside the sound button, and ending a session by accident
    // cannot be undone.
    onStopSession: () => setConfirmingStop(true),
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-black">
      {/*
        THE one bar every page wears, with this screen's cluster in its middle — see
        SiteBar for the frame and ScreenControls for the cluster. IN THE FLOW, not an
        overlay: the bar sits ABOVE the regions and they get everything below it, so no
        strip of video ever plays underneath chrome. And FULLY BLACK — a screen is video
        edge to edge, and a translucent bar over black is just a worse black.

        The bar RIDES FULLSCREEN too. It used to be removed there (`hidden`) — until the
        screen-code bubble became the way a claimed screen changes hands, at which point
        hiding the bar took the code off the glass exactly when a TV is most likely to be
        watched from across a room. The regions still get everything below the bar;
        fullscreen just makes "below the bar" taller.

        Every action is passed in rather than reached for, so both halves render
        standalone in their checks — no socket, no router, no session.
      */}
      <SiteBar
        opacity={1}
        code={pairing}
        back={back}
        faps={<FapsPill onOpen={() => setFapsOpen("pill")} />}
        /* The ☰'s top group at every width — who you are, where the desktop bubble
           used to be. */
        account={
          <MenuAccountItems
            onSignIn={() => setMenuAuthOpen("login")}
            onGold={setMenuGoldOpen}
            onFaps={() => setFapsOpen("menu")}
            onEarnFaps={() => setMenuAuthOpen("faps")}
          />
        }
        /* THIS SCREEN's controls, in the menu's page slot (its bottom group). They fold
           out of the bar at `sm:` and there is nowhere on a wall to float them — every
           corner of every tile already wears something — so this documented slot is
           where they land. See ScreenMenuItems. */
        menu={<ScreenMenuItems {...foldingControls} />}
        center={
          <ScreenControls
            {...foldingControls}
            // Offered only while something is playing — sound is a property of the
            // playback, and a screen between views has none to be heard.
            // Clicking still toggles, exactly as it always did — but a boolean has one
            // inverse and a four-mode enum has three, so the button and the remote's
            // command both resolve "unmute" through soundModeFromWire or they will
            // eventually restore different things.
            mute={
              view
                ? {
                    on: muted,
                    mode: soundMode,
                    expanded: audioOpen,
                    onToggle: () =>
                      setSoundMode((m) =>
                        soundModeFromWire(!soundModeMuted(m), lastAudibleRef.current),
                      ),
                    onOpen: openAudio,
                    onOpenEnd: closeAudio,
                    // Built here and handed down: the cluster stays presentational, and
                    // the panel lands beside the button it belongs to rather than at the
                    // far end of the document.
                    panel: audioOpen ? (
                      <MasterAudioPanel
                        mode={soundMode}
                        onMode={setSoundMode}
                        master={master}
                        onMaster={setMaster}
                        soloed={soloAudible}
                        eligibleCount={eligibleCount}
                        channels={CHANNELS}
                        current={radio.current}
                        playing={radio.playing}
                        radioLevel={radio.level}
                        onRadioLevel={radio.setLevel}
                        onChannel={radio.step}
                        // Pressing PLAY on a muted screen is the same ask as choosing
                        // Radio. Nothing may sound through `muted`, and a transport
                        // that ran silent would be the lie-with-a-progress-bar the
                        // hook refuses — so the press moves the MODE, and the edge
                        // effect above presses play. Every other press stays the
                        // transport's own.
                        onPlayPause={() => {
                          if (!radio.playing && soundModeMuted(soundMode)) {
                            trackAudioMode("radio");
                            setSoundMode("radio");
                            return;
                          }
                          radio.toggle();
                        }}
                        onPartner={(c) => setPartner(c.partner)}
                        onClose={() => {
                          clearAudioTimers();
                          setAudioOpen(false);
                        }}
                      />
                    ) : null,
                  }
                : null
            }
            fullscreen={
              canFullscreen ? { on: isFullscreen, onToggle: toggleFullscreen } : null
            }
          />
        }
      />

      {(frame ?? unframed)(
      <div
        ref={glassRef}
        className="relative min-h-0 flex-1 overflow-hidden"
        // pan-y: the vertical axis stays the browser's (the scrolls scroll), the
        // horizontal one reaches the flick handler — see useHorizontalSwipe.
        style={{ cursor: cursorHidden ? "none" : "auto", touchAction: "pan-y" }}
        {...swipe}
      >
        {view && Stage ? (
          /* A kind's own stage, handed everything the screen owns. The sound verdict
             is the stage's to compute per tile through soundsHere, the same as the
             default stage below; the MODE stays this screen's, so the bar's red button
             and the remote's mute silence a stage exactly as they silence a view. */
          <Stage
            // Keyed by the VIEW as well as the epoch: two scrolls are one component, and a
            // picker hop from one to the other must not carry the first one's pages, picks
            // or saved-game state across (seen 2026-09-19, Goonette → Build a Slut).
            key={`${stageOf}:${stageEpoch}`}
            viewId={stageOf!}
            view={view}
            room={room}
            follow={follow}
            registry={registryRef.current}
            fapBus={fapBusRef.current}
            syncBus={syncBusRef.current}
            paused={tilesPaused}
            soundMode={soundMode}
            master={master}
            portrait={portrait}
            signedIn={!!user}
            onAutoMuted={() => {
              track("sound_refused");
              setSoundMode("muted");
            }}
            onView={recordView}
            autoScrollSec={autoScrollSec}
            onGold={setMenuGoldOpen}
            onPoint={(id) => void pointTo(id)}
          />
        ) : view && drawnRegions && drawnRegions.length === 0 ? (
          /* An empty view used to draw nothing — a blank black stage that looked like a
             hang. RegionCanvas already says when there is nothing to compose; the play
             path needs the same honesty so Play on a guest Build View is not "all black". */
          <EmptyViewStage />
        ) : view && drawnRegions ? (
          drawnRegions.map((region) => {
            // A follower plays its leader's clip and runs no feed of its own — see
            // RegionTile.leader for why the pair counts as one clip.
            const leader = syncLeaderOf(region, view.regions);
            return (
            <RegionTile
              key={region.id}
              region={region}
              leader={leader}
              registry={registryRef.current}
              syncBus={syncBusRef.current}
              driven={follow ?? undefined}
              paused={tilesPaused}
              // The AND of sound's two owners, through soundsHere — where the
              // layout's per-region soundOn (absent = silent) is tested FIRST and
              // unconditionally, and the room's four-valued mode second. A muted
              // screen stays silent whatever the view says, and in Play 1 at most
              // one panel can match however `audibleId` was arrived at.
              // ...and a spent allowance outranks both owners: the shrunk wall is silent.
              muted={exhausted || !soundsHere(soundMode, region, audibleId)}
              // One gain law for every source on this screen: the master fader
              // multiplies the layout's stored level, exactly as it multiplies the
              // radio channel's own.
              volume={sourceGain(master, regionVolume(region))}
              // The browser refused sound: show muted, because that is what is
              // playing. The remote learns it through the same report — and it is
              // counted, because a sound feature that looks on and stays silent
              // is invisible in every other number.
              onAutoMuted={() => {
                track("sound_refused");
                setSoundMode("muted");
                // The refusing tile may be the one Play 1 just chose. Left standing,
                // the pick re-arms the same refusal the next time the wall is unmuted.
                setSoloId(null);
              }}
              onView={recordView}
              // The ring lights for the hovered tile, for a link-menu peek, and for
              // every PEER of a hovered sync group — in the group's tint.
              selected={
                hovered === region.id ||
                linkPeek === region.id ||
                ((hovered ?? linkPeek) !== null &&
                  (peerIds.get((hovered ?? linkPeek)!) ?? []).includes(region.id))
              }
              ringTint={syncTints.get(region.id)}
              // Only the account half is answered here. Whether the clip ON SCREEN can
              // be fapped is the overlay's own business: a square mixes catalog picks
              // and uploads in one rotation.
              signedIn={!!user}
              fapBus={fapBusRef.current}
              fapSurface="screen"
              onFapped={room ? (id) => room.sendFap(id) : undefined}
              onMouseEnter={(e) => {
                // TWO TAPS THAT ARE NOT SELECTIONS, and this is where both would become
                // one — a finger's tap synthesises a mouseenter, and that mouseenter is
                // what selects a tile. See crownHit for why the crown cannot call this
                // off itself, and overlayOpen for why a dismissing tap has to be spotted
                // back at pointerdown. On a MOUSE neither guard fires on the way in:
                // reaching the crown means crossing the tile first, and React sends no
                // enter for a move that stays inside the tile.
                const tap = touchTap.current;
                if (tap && tap.id === region.id && tap.dismiss) return;
                if (crownHit(e.target)) return;
                setHovered(region.id);
                // First proof somebody is at this glass — start the catalog fetches
                // the chrome's labels and the feed popup will need.
                setWantFeedData(true);
              }}
              onMouseLeave={(e) => {
                // ON TOUCH A TAP OWNS SELECTION, so the invented mouse burst does not get
                // to take it away — measured: a tap can be followed by mouseouts of no
                // fixed shape or timing, which is how a freshly selected tile could go
                // dark on its own and how pressing the crown could drop a selection it
                // never asked about. On a MOUSE this still fires normally, and leaving
                // INTO the crown is not leaving the tile at all.
                if (touchPointer.current) return;
                if (crownHit(e.relatedTarget)) return;
                setHovered((h) => (h === region.id ? null : h));
              }}
              /*
               * A FINGER TOGGLES; A DOUBLE OPENS. On a mouse the click opens the popup
               * outright because HOVER already showed the chrome — the pointer proved
               * its target on the way in. A finger has no hover, so it gets a contract of
               * its own: tap an unselected tile and it selects (the full hover treatment,
               * audio aim included), tap the tile already selected and it lets go, and
               * two taps inside DOUBLE_TAP_MS open the popup, landing selected. Nothing
               * is held back by a timer — the toggle happens on the first tap and a quick
               * second one simply wins, so double-tapping a selected tile flickers once.
               * That is the trade, and it beats making every selection wait 300ms.
               *
               * THE DISCRIMINATOR IS READ AT POINTERDOWN AND NEVER AT CLICK. By click
               * time the tap's own synthetic mouseenter has already moved `hovered` (that
               * is the whole mechanism), and any overlay the tap dismissed has already
               * closed itself. Both are captured here, ahead of the emulation, on iOS and
               * Android alike. A tap that landed on the crown records nothing: the crown's
               * click stops propagation, so a record written for it would never be cleared
               * and the NEXT tap would be judged on it.
               */
              onPointerDown={(e) => {
                touchPointer.current = e.pointerType === "touch";
                touchTap.current =
                  e.pointerType === "touch" && !crownHit(e.target)
                    ? { id: region.id, wasSelected: hovered === region.id, dismiss: overlayOpen }
                    : null;
              }}
              onClick={() => {
                const tap = touchTap.current;
                touchTap.current = null;
                if (tap !== null) {
                  // The tap that dismissed an overlay is spent on the dismiss.
                  if (tap.dismiss) return;
                  const now = performance.now();
                  const double =
                    lastTap.current.id === region.id && now - lastTap.current.at < DOUBLE_TAP_MS;
                  lastTap.current = { id: region.id, at: double ? 0 : now };
                  if (!double) {
                    // ONE TAP TOGGLES. There was no letting go at all before this:
                    // `hovered` cleared on mouseleave, which a finger never sends.
                    if (tap.wasSelected) setHovered((h) => (h === region.id ? null : h));
                    else {
                      setHovered(region.id);
                      setWantFeedData(true);
                    }
                    return;
                  }
                  // THE DOUBLE LANDS SELECTED, whatever its own first tap left behind.
                  setHovered(region.id);
                  setWantFeedData(true);
                }
                // ON THE WALL THE CLICK OPENS THE BUY DIALOG. The feed popup is an
                // edit surface and a square is not yours to edit — it is BOUGHT, so
                // the same gesture that picks a feed anywhere else starts a purchase
                // here. Same slot in the tap contract: mouse click outright, finger
                // double-tap, selection untouched.
                // A MEMBER of a room edits nothing: the tap lights the ring and that is all.
                if (!canEdit) return;
                if (wallMode) {
                  setWallBuyRegionId(region.id);
                  return;
                }
                // A CLICK OPENS THIS REGION'S FEED POPUP — collections and keywords
                // for the one tile under the pointer, right on the glass. Fapping is
                // the crown's alone now (bottom-right; it stops its own propagation),
                // and the FULL editor stays behind the bar's pencil.
                track("region_feed_opened");
                setFeedEditRegionId(region.id);
              }}
            >
              {/* The wall wears the money badge INSTEAD of the editing chrome, because
                  a billboard's price belongs on the glass and none of the sound/sync/
                  loop controls apply to a square somebody bought. It fades once the
                  screen goes quiet: the price has to be READ, not stared at, and eleven
                  plates sitting on top of the pictures forever is a board you stop
                  seeing. Any movement brings them straight back. */}
              {wallMode ? (
                <WallRegionChrome
                  square={wall.squares.get(region.id) ?? null}
                  idle={wallBadgesIdle}
                  currency={wall.currency}
                />
              ) : null}
              {/* Hover ALONE, unlike the chrome below: a link-menu peek lights another
                  tile's ring from a pointer that is over the MENU, and telling that tile
                  to "click to edit" would be pointing at something the hand is not on. */}
              {canEdit && !wallMode && hovered === region.id ? <RegionEditHint /> : null}
              {/* Hover-or-locked, not hover alone: the chrome pins itself open while
                  its link menu (portaled below the tile) or a volume drag is live.
                  Or PEEKED: a link-menu row under the pointer mounts this tile's
                  chrome too, so the candidate shows its name and feed as if hovered. */}
              {canEdit &&
              !wallMode &&
              (hovered === region.id || chromeLock === region.id || linkPeek === region.id) ? (
                <RegionHoverChrome
                  region={region}
                  topInset={chromeInset(region)}
                  regions={view.regions}
                  index={view.regions.findIndex((r) => r.id === region.id)}
                  collections={collections}
                  screenMuted={muted}
                  syncTint={syncTints.get(region.id)}
                  onEditRegion={(leaderId, patch, opts) =>
                    applyRegionEdit(leaderId, patch, opts)
                  }
                  onToggleLoop={toggleRepeat}
                  // The same two transforms the editor's link button runs — a sync made
                  // on the glass is the same mutation, only the destination differs
                  // (a wall PUT instead of onRegions).
                  onLink={(target) =>
                    applyRegionsEdit((rs) => linkRegions(rs, region.id, target, "normal"))
                  }
                  onUnlink={() =>
                    applyRegionsEdit((rs) => unlinkAdoptShared(rs, region.id))
                  }
                  onToggleMirror={() =>
                    applyRegionsEdit((rs) => toggleMirror(rs, region.id))
                  }
                  onLockChange={(locked) => {
                    setChromeLock(locked ? region.id : null);
                    // A releasing lock means the link menu is gone — no peek without it.
                    if (!locked) setLinkPeek(null);
                  }}
                  onCandidateHover={setLinkPeek}
                />
              ) : null}
            </RegionTile>
            );
          })
        ) : (
          <WaitingForConfig />
        )}

        {!connected && <ReconnectingBadge />}

        {tilesPaused && view && <PausedBadge />}

        {identifying && view && <IdentifyOverlay name={view.name} />}

        {/* Only with the switch on AND a code to encode. Both halves matter: the first is
            the setting, the second is that a screen mid-announce has nothing to point a
            camera at yet and a QR of an empty string is a QR that scans to nothing. */}
        {party?.settings.partyQr && party.url ? (
          <PartyBadge url={party.url} heldUntil={party.heldUntil} />
        ) : null}

        {/* THE GLASS'S OWN CHROME. The Now Showing chip at every width (the bar lost its
            bubble; this is the one component), and below `lg:` the phone's action
            cluster too. Inside the media box rather than beside it, so it floats over
            the glass instead of taking a row from the wall. */}
        <ConfirmDialog
          open={confirmingStop && sessionRunning}
          title="End this goon session?"
          message="The timer stops and your recap opens."
          confirmLabel="End session"
          onConfirm={() => {
            setConfirmingStop(false);
            stopSession();
          }}
          onCancel={() => setConfirmingStop(false)}
        />
        <ScreenThumbBar {...foldingControls} idle={thumbIdle} allowance={meterOn && !exhausted ? allowanceGauge : null} infoFades={infinityMode} />
        {/* THE SPENT GAUGE STAYS. The thumb bar fades when the screen goes quiet, which is
            right for a readout — and wrong for the one line that explains why a sixteen-
            tile wall is suddenly four silent ones. So the spent form mounts here, on its
            own, in the thumb bar's bottom-left seat, and does not fade. */}
        {meterOn && exhausted ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-30 flex items-center p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            <div className="pointer-events-auto flex items-center rounded-full border border-amber-400/30 bg-[var(--color-ink)]/85 p-0.5 backdrop-blur">
              {allowanceGauge}
            </div>
          </div>
        ) : null}
      </div>,
      room,
      )}

      {partner ? (
        <PartnerDialog partner={partner} source="channel_row" onClose={() => setPartner(null)} />
      ) : null}

      {settingsOpen && party ? (
        <ScreenSettingsDialog
          settings={party.settings}
          onChange={party.onChange}
          heldUntil={party.heldUntil}
          onRelease={party.onRelease}
          onClose={() => setSettingsOpen(false)}
        />
      ) : null}

      {summary && (
        <SessionSummaryModal
          session={summary.session}
          onClose={() => setSummary(null)}
          username={user?.username}
          /* The same claim /session/stop made: a screen acts for its DEVICE's owner, which
             on a claimed TV is not the browser's own identity. Without it the summary read
             is made as the television and 404s on a session the phone owns. */
          deviceId={deviceId}
        />
      )}

      {/* The wall's first-time explainer, above everything else on the glass. */}
      {guide ? (
        <SurfaceGuide
          surface={guide.surface}
          source={guide.source}
          /* The wall's for-sale copy mentions the free pool only while a free square
             actually EXISTS on the board — the free-claims rule (the copy never
             promises a dark switch), carried from the deleted wall intro's `anyFree`
             prop into the guide as a content override. Read at render, so a freebie
             appearing mid-read updates the sentence. The same rule for FAPS: the board
             says which currency it sells in, and the guide speaks that one. */
          content={
            // Goonette of the Hill: tap is the pick, not unmute — its own card whenever
            // that scroll is live. Under FAPS the cards speak the FAPS till (one wall
            // card for both boards for now); otherwise the wall's card is picked by
            // which wall and whether a freebie exists on it.
            guide.surface === "infinity" && stageOf && infinityIdOfView(stageOf) === "battle"
              ? INFINITY_BATTLE_GUIDE
              : wall.currency === "faps"
                ? guide.surface === "wall"
                  ? wall.premiumOnly
                    ? PREMIUM_WALL_GUIDE_FAPS
                    : WALL_GUIDE_FAPS
                  : guide.surface === "screen"
                    ? namedScreenGuide(SCREEN_GUIDE_FAPS, shownViewName)
                    : undefined
                : guide.surface === "wall"
                  ? wallGuide(wall.premiumOnly, [...wall.squares.values()].some((sq) => sq.freeClaim))
                  : guide.surface === "screen"
                    ? namedScreenGuide(GUIDES.screen, shownViewName)
                    : undefined
          }
          onClose={() => setGuide(null)}
        />
      ) : null}

      {/* The wall's buy dialog — same top-level placement and for the same bubbling
          reason as the feed popup below it. `premiumOnly` is the Premium Wall's gate,
          asked inside the dialog rather than at the tile: the tile's click is the same
          on every wall, and what a non-member sees is the dialog's business. */}
      {wallBuyRegionId ? (
        <WallBuyDialog
          regionId={wallBuyRegionId}
          square={wall.squares.get(wallBuyRegionId) ?? null}
          selling={wall.enabled}
          currency={wall.currency}
          premiumOnly={wall.premiumOnly}
          onClose={() => setWallBuyRegionId(null)}
        />
      ) : null}

      {/* The region feed popup — top level beside the other sheets, never inside the
          tile: React events bubble through the REACT tree portal or not, so a child
          dialog would hand every click back to the tile's onClick that opened it.
          The find guard covers the frame before the vanish effect above lands. */}
      {feedEditRegionId && view
        ? (() => {
            const r = view.regions.find((x) => x.id === feedEditRegionId);
            return r ? (
              <RegionFeedDialog
                region={r}
                regions={view.regions}
                index={view.regions.findIndex((x) => x.id === r.id)}
                collections={collections}
                tags={contentTags}
                loading={collectionsLoading || tagsLoading}
                onSetSource={(leaderId, source) => {
                  // The same event and the same prop discipline as the editor's pick
                  // (see SimpleEditor's setSource): kind + how many regions the edit
                  // landed on, facet slugs only for keywords, and for a collection a slug
                  // (built-in) or a pseudonymous id (everyone else's) — never a title.
                  track("region_source_set", {
                    kind: source.kind,
                    regions: 1,
                    ...(source.kind === "keywords"
                      ? facetCombo(keywordGroups(source).flat())
                      : {}),
                    ...regionSourceCollectionProps(source, collections),
                  });
                  applyRegionEdit(leaderId, { source });
                }}
                onClose={() => setFeedEditRegionId(null)}
              />
            ) : null;
          })()
        : null}

      {/* The same picker the remote opens, from this screen's own Now Showing bubble.
          Pointing here is the remote's act minus the nudge: this IS the screen, so it
          assigns itself and then does what the reload command would have told it to. */}
      {picker ? (
        <WhatsPlayingSheet
          showing={viewId ?? null}
          onClose={() => setPicker(false)}
          onPoint={async (id) => {
            await pointTo(id);
            setPicker(false);
          }}
          // A sheet pick of a collection mints a temp view too — remember its origin so
          // the arrows can keep cycling from that collection's slot in the list.
          onMinted={(id, meta) => mintedFromRef.current.set(id, meta.collectionId)}
        />
      ) : null}

      {menuGoldOpen ? (
        <GoldDialog source={menuGoldOpen} onClose={() => setMenuGoldOpen(null)} />
      ) : null}
      {fapsOpen ? <FapsDialog source={fapsOpen} onClose={() => setFapsOpen(null)} /> : null}
      {/* The crown's cap moment, sold — beside the dialog it opens. Small and auto-dismissing:
          this is a television as often as a phone. */}
      <FapsCapNudge onGoPremium={() => setMenuGoldOpen("faps_cap")} />
      {/* The bidding sink's other side: a square of yours was taken — take it back. */}
      <FapsUnseatedCard />
      {menuAuthOpen ? (
        <AuthModal
          initialMode={menuAuthOpen === "faps" ? "register" : "login"}
          reason={menuAuthOpen === "faps" ? FAPS_AUTH_REASON : undefined}
          onClose={() => setMenuAuthOpen(null)}
        />
      ) : null}
    </div>
  );
}

function EmptyViewStage() {
  // Same chip grammar as RegionCanvas's empty canvas — one clear line, no redesign.
  return (
    <div className="flex h-full w-full items-center justify-center bg-black">
      <span className="rounded-md bg-[var(--color-ink)]/70 px-3 py-1.5 text-xs text-zinc-500">
        No regions yet
      </span>
    </div>
  );
}

function WaitingForConfig() {
  return (
    <div className="flex h-full w-full items-center justify-center">
      <div className="flex flex-col items-center gap-3 text-zinc-500">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-[var(--color-edge)] border-t-[var(--color-accent)]" />
        <span className="text-sm tracking-wide">Waiting for config…</span>
      </div>
    </div>
  );
}

function ReconnectingBadge() {
  return (
    <div className="pointer-events-none absolute right-3 top-3 z-40 flex items-center gap-2 rounded-full border border-[var(--color-edge)] bg-[var(--color-panel)]/80 px-3 py-1.5 backdrop-blur">
      <span className="h-2 w-2 animate-pulse rounded-full bg-[var(--color-accent)]" />
      <span className="text-[11px] font-medium tracking-wide text-zinc-300">
        Reconnecting…
      </span>
    </div>
  );
}

function PausedBadge() {
  return (
    <div className="pointer-events-none absolute left-3 top-3 z-40 flex items-center gap-2 rounded-full border border-[var(--color-edge)] bg-[var(--color-panel)]/80 px-3 py-1.5 backdrop-blur">
      <svg viewBox="0 0 24 24" className="h-3 w-3 fill-[var(--color-accent-soft)]">
        <rect x="6" y="5" width="4" height="14" rx="1" />
        <rect x="14" y="5" width="4" height="14" rx="1" />
      </svg>
      <span className="text-[11px] font-medium tracking-wide text-zinc-300">Paused</span>
    </div>
  );
}

function IdentifyOverlay({ name }: { name: string }) {
  return (
    <div className="pointer-events-none absolute inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 animate-pulse bg-[var(--color-accent)]/25" />
      <div className="absolute inset-0 border-[6px] border-[var(--color-accent)]" />
      <div className="relative flex flex-col items-center gap-2 rounded-2xl border border-[var(--color-accent)] bg-black/70 px-10 py-6 backdrop-blur">
        <span className="text-xs uppercase tracking-[0.3em] text-[var(--color-accent-soft)]">
          This screen
        </span>
        <span className="text-4xl font-semibold text-white">{name}</span>
      </div>
    </div>
  );
}
